/* =====================================================================
 * Zustand Store — 대화, 메시지, 스트리밍, 토스트 통합 관리
 * ===================================================================== */

import { create } from 'zustand';
import { v4 as uuid } from 'uuid';
import { useUIStore } from './uiStore';
import { useWorkspaceNavigationStore } from './workspace/navigationStore';
import type { Toast } from './uiStore';
import type {
  Conversation,
  ChatMessage,
  Reaction,
  StreamEvent,
  AgentCompletedData,
  BrowserLiveViewPayload,
  FileAttachment,
  AgentStreamResponse,
  AgentInProgressData,
  AgentPhase,
  WorkspaceMode,
  WorkspaceScope,
  WorkspaceKind,
  WorkspaceFile,
  AttachmentInfo,
  GeneratedFileInfo,
  ActiveFileRef,
} from '@auw/workspace-utils';
import {
  streamAgent,
  cancelAgentStream,
  uploadFiles,
  downloadCloudFile,
  downloadMailAttachment,
  downloadCollaborationAttachment,
  downloadMessengerAttachment,
} from '@auw/api-client';
import { getUserLocale, isMobileViewport } from '@auw/workspace-utils';
import { useUserStore } from './userStore';
import { useTeamStore } from './teamStore';
import { useReboModeStore } from './reboModeStore';
import { loadFromBackend, loadRecentFromBackend } from '@auw/api-client';
import { syncQueue } from '@auw/api-client';
import type { WorkspaceSSEvent } from '@auw/api-client';
import * as wsApi from '@auw/api-client';
import { conversationToCreateParams, itemToConversation } from '@auw/api-client';
import { collabWS } from '@auw/api-client';
import { emitAppEvent } from './events';
import {
  getErrorMessage,
  isNotFoundHttpError,
  isUnauthorizedHttpError,
  isWorkspaceItemAccessDeniedError,
  isWorkspaceItemUnavailableError,
  dispatchAuthSessionInvalidated,
} from '@auw/workspace-utils';
import { buildConversationSettings } from '@auw/workspace-utils';
import { isAnswerChannel, isThinkingChannel, isWidgetChannel } from '@auw/workspace-utils';
import {
  EMPTY_CONVERSATION_LIVE_VIEW_RUNTIME,
  EMPTY_CONVERSATION_RUNTIME,
  clearConversationRuntimeMap,
  getConversationRuntime,
  isConversationRuntimeBusy,
  isConversationRuntimeStreaming,
  patchConversationRuntimeMap,
  type ConversationRuntimeMap,
  type ConversationRuntimeState,
} from './workspace/runtime/runtimeState';
import {
  isBrowserLiveViewPayload,
  normalizeConversationLiveView,
} from '@auw/workspace-utils';
import {
  GROUP_AGENT_MENTION_RE,
  resolveBillingContext,
  resolveSendMessageTarget,
} from './workspace/runtime/sendMessageFlow';
import {
  collectDescendantConversationIds,
  isDescendantConversation,
} from './workspace/domain/conversationTree';
import {
  mergeConversationBatch,
  mergeConversationEntity,
} from './workspace/domain/conversationMerge';
import {
  applyConversationMemoryPolicy,
  clampMessages,
  persistMessagesToCache,
} from './workspace/domain/conversationMemory';
import {
  FAST_BOOTSTRAP_RECENT_LIMIT,
  readWorkspaceBootstrapCache,
  writeWorkspaceBootstrapCache,
  type BootstrapLoadResult,
} from './workspace/sync/bootstrapCache';
import {
  buildRemoteAttachment,
  parseAttachmentFilesFromMeta,
} from './workspace/sync/attachmentMeta';
import {
  clearSelectionLoadingIndicator,
  runConversationSelectionEffects,
} from './workspace/effects/selectionEffects';
import {
  cancelConversationMemorySweep,
  scheduleConversationMemorySweep as scheduleConversationMemorySweepImpl,
} from './workspace/effects/memorySweep';
import { handleTokensExhausted } from './workspace/domain/tokenExhaustion';
import {
  clearMessageHistoryCache,
  fetchMessageHistory,
} from './workspace/sync/messageHistoryCache';
import i18n from '@auw/i18n';

/* ------------------------------------------------------------------ */
/* Toast                                                               */
/* ------------------------------------------------------------------ */

export type { Toast } from './uiStore';

/* ------------------------------------------------------------------ */
/* State                                                               */
/* ------------------------------------------------------------------ */

export type { WorkspaceMode } from '@auw/workspace-utils';

type ModeItemCountMap = Partial<Record<WorkspaceMode, number>>;

interface ChatState {
  /* data */
  conversations: Record<string, Conversation>;
  conversationOrder: string[];
  workspaceOrder: string[];
  activeId: string | null;

  /* ui */
  sidebarOpen: boolean;
  darkMode: boolean;
  activeMode: WorkspaceMode;

  /* toast */
  toasts: Toast[];

  /* per-conversation runtime */
  conversationRuntimes: ConversationRuntimeMap;

  /* global upload (document/theme 등 비-대화 업로드용) */
  isUploading: boolean;

  /* file preview */
  previewFile: FileAttachment | null;
  previewFullscreen: boolean;
  /** 좁은 영역에서 현재 표시 패널: 'preview' | 'chat' */
  narrowPreviewView: 'preview' | 'chat';
  /** 실제 콘텐츠 영역이 narrow 상태인지 (ResizeObserver로 갱신) */
  isNarrowLayout: boolean;

  /* 대화 전환 시 로딩 중인 conversation id (EmptyState 깜빡임 방지) */
  loadingConversationId: string | null;

  /* 사이드바 무한 스크롤 */
  totalItemCount: number;
  totalItemCountByMode: ModeItemCountMap;
  loadingMoreItems: boolean;
  workspaceHydrated: boolean;

  /* session files — 현재 세션에 연결된 파일 (업로드 + 에이전트 생성) */
  sessionFiles: WorkspaceFile[];

  /* actions – conversations */
  createConversation: (opts?: {
    mode?: WorkspaceMode;
    title?: string;
    scope?: WorkspaceScope;
    kind?: WorkspaceKind;
    teamId?: string | null;
    parentId?: string | null;
    activate?: boolean;
    startPagePending?: boolean;
    startPageState?: Conversation['startPageState'];
  }) => string;
  deleteConversation: (id: string, options?: { cascade?: boolean }) => void;
  renameConversation: (id: string, title: string) => void;
  setConversationStartPagePending: (id: string, pending: boolean) => void;
  setConversationStartPageState: (
    id: string,
    patch: Partial<Conversation['startPageState']>,
  ) => void;
  setActiveConversation: (id: string | null, options?: { syncMode?: boolean }) => void;
  moveConversationWorkspace: (id: string, params: {
    parentId: string | null;
    scope: WorkspaceScope;
    teamId?: string | null;
    insertBeforeId?: string;
    insertAfterId?: string;
  }) => void;

  /* actions – ui */
  toggleSidebar: () => void;
  setSidebarOpen: (v: boolean) => void;
  toggleDarkMode: () => void;
  setActiveMode: (mode: WorkspaceMode) => void;

  /* actions – file preview */
  setPreviewFile: (file: FileAttachment | null) => void;
  setPreviewFullscreen: (v: boolean) => void;
  togglePreviewFullscreen: () => void;
  setNarrowPreviewView: (v: 'preview' | 'chat') => void;
  setNarrowLayout: (v: boolean) => void;

  /* actions – toast */
  addToast: (t: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;

  /* actions – messaging */
  sendMessage: (
    content: string,
    attachments?: FileAttachment[],
    options?: {
      forceInvokeAgent?: boolean;
      modeOverride?: WorkspaceMode;
      /**
       * 백엔드로 보내는 ``workspace_type`` 만 덮어쓴다. UI 대화 모드
       * (``modeOverride`` / conversation.mode / activeMode) 와 분리하기 위한
       * 채널 — 확장자별 특화 문서 에이전트(docx/xlsx/hwp/ppt/pdf-agent)가
       * UI 는 그대로 ``doc-agent`` 표면을 쓰되 서버 라우팅만 포맷별
       * 엔드포인트로 보낼 때 사용. 미지정 시 ``modeOverride`` 로 결정된
       * 워크스페이스 모드를 그대로 ``workspace_type`` 으로 보낸다.
       */
      workspaceTypeOverride?: string;
      reboMode?: 'auto' | 'fast' | 'plan' | 'skill';
      activeSkillIds?: string[];
      /** AgentRequest.active_selection 으로 전달. doc-agent 등에서 사용 */
      activeSelection?: Record<string, unknown> | null;
      /** AgentRequest.metadata 로 전달 (네임스페이스: doc_agent 등) */
      requestMetadata?: Record<string, unknown> | null;
      /** 그룹채팅에서 @멘션된 팀원 user_id 목록 — DM 멘션 알림용 */
      mentionedUserIds?: string[];
    },
  ) => void;
  regenerateMessage: (
    conversationId: string,
    targetMessageId: string,
    forceMode: 'fast' | 'plan' | 'skill' | null,
  ) => void;
  stopStreaming: (conversationId?: string | null) => void;
  toggleReaction: (conversationId: string, messageId: string, emoji: string, userId: string) => void;
  applyConversationLiveViewEvent: (
    conversationId: string,
    liveView: BrowserLiveViewPayload,
    options?: { autoOpen?: boolean; running?: boolean },
  ) => void;
  setConversationLiveViewOpen: (conversationId: string, open: boolean) => void;
  setConversationLiveViewRunning: (conversationId: string, running: boolean) => void;
  /** 내 프로필(이름/아바타) 변경 시 그룹 멤버 정보 즉시 반영 */
  syncCurrentUserProfile: (payload: {
    userId: string;
    name?: string | null;
    email?: string | null;
    picture?: string | null;
  }) => void;

  /* actions – session files */
  loadSessionFiles: (sessionId: string) => Promise<void>;
  loadConversationFiles: (conversationId: string) => Promise<void>;
  removeSessionFile: (fileId: string) => Promise<void>;
  refreshSessionFiles: () => Promise<void>;

  /* actions – backend sync */
  loadFromBackendApi: () => Promise<void>;
  loadMoreItems: (options?: { mode?: WorkspaceMode }) => Promise<void>;
  prefetchMessages: (id: string) => void;
  /** SSE 이벤트 기반 단건 업데이트 (전체 리로드 없이) */
  applyRemoteEvent: (evt: WorkspaceSSEvent) => void;
  /** 로컬 상태에서만 대화를 제거 (API 호출 없음, 직접 API 삭제 후 사용) */
  removeConversationLocal: (id: string) => void;

  /* getters */
  activeConversation: () => Conversation | null;
  activeMessages: () => ChatMessage[];
  isStreaming: (conversationId?: string | null) => boolean;
  isUploadingConversation: (conversationId?: string | null) => boolean;
  setUploading: (v: boolean) => void;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/**
 * 같은 ChatArea 표면을 공유하는 ``WorkspaceMode`` 들.
 *
 * ``ChatArea`` / ``WorkspaceSplitLayout`` / ``AgentContent`` 는 통합 채팅
 * 표면 하나에서 ``agent`` 본 모드와 ``widget`` / ``feed`` / ``tools`` 같은
 * pseudo-mode 들을 같은 conversation 으로 그린다 (``sendMessage`` 본체의
 * ``widget|feed|tools → agent`` normalization 과 의미 동치). 다른 모드
 * (``doc-agent``, ``live-agent``, ``document``, …) 는 자체 페이지에 별도로
 * 그려지므로 같은 표면이 아니다.
 *
 * ``activeConversation`` getter 의 mode-mismatch 가드에서 사용.
 */
const AGENT_CHAT_SURFACE_MODES: ReadonlySet<WorkspaceMode> = new Set([
  'agent',
  'widget',
  'feed',
  'tools',
]);

function sameChatSurface(a: WorkspaceMode, b: WorkspaceMode): boolean {
  if (a === b) return true;
  if (AGENT_CHAT_SURFACE_MODES.has(a) && AGENT_CHAT_SURFACE_MODES.has(b)) {
    return true;
  }
  return false;
}

function makeConversation(
  title = i18n.t('common:chat.newConversation'),
  mode: WorkspaceMode = 'agent',
  opts?: {
    scope?: WorkspaceScope;
    kind?: WorkspaceKind;
    teamId?: string | null;
    parentId?: string | null;
    startPagePending?: boolean;
    startPageState?: Conversation['startPageState'];
  },
): Conversation {
  const now = Date.now();
  const scope = opts?.scope ?? 'personal';
  const kind = opts?.kind ?? 'page';
  const fallbackTitle = kind === 'folder'
    ? i18n.t('common:chat.newFolder')
    : (
      mode === 'agent' || mode === 'message'
        ? i18n.t('common:chat.newConversation')
        : i18n.t('common:page.newPage')
    );
  return {
    id: uuid(),
    title: title || fallbackTitle,
    mode,
    workspaceScope: scope,
    workspaceKind: kind,
    workspaceTeamId: scope === 'team' ? (opts?.teamId ?? null) : null,
    workspaceParentId: opts?.parentId ?? null,
    startPagePending: opts?.startPagePending ?? false,
    startPageState: normalizeStartPageState(mode, opts?.startPageState),
    messages: [],
    historyLoaded: true,
    createdAt: now,
    updatedAt: now,
    pinnedAt: null,
    sharedGroup: null,
    liveView: null,
  };
}

function buildFirstMessageFallbackTitle(content: string): string {
  const normalized = content.replace(/\s+/g, ' ').trim();
  if (normalized.length <= 25) return normalized;
  return normalized.slice(0, 24).trimEnd() + '…';
}

/** 스킬 모드는 현재 대화 컨텍스트에 한정된 1회성 선택 — 새 대화/대화 전환 시 Auto 복귀 */
function resetSkillModeToAuto(): void {
  const rebo = useReboModeStore.getState();
  if (rebo.mode === 'skill') {
    rebo.setMode('auto');
  }
}

function makeUserMessage(
  content: string,
  attachments?: FileAttachment[],
  sender?: { id: string; name: string | null } | null,
  metadata?: Record<string, unknown>,
): ChatMessage {
  return {
    id: uuid(),
    role: 'user',
    content,
    attachments,
    createdAt: Date.now(),
    isStreaming: false,
    streamEvents: [],
    senderId: sender?.id ?? null,
    senderName: sender?.name ?? null,
    ...(metadata ? { metadata } : {}),
  };
}

function mergeUploadedAttachmentPreviews(
  previous: FileAttachment[] | undefined,
  next: FileAttachment[],
): FileAttachment[] {
  if (!previous?.length || next.length === 0) return next;

  const queuedBySignature = new Map<string, FileAttachment[]>();
  previous.forEach((attachment) => {
    const signature = `${attachment.name}::${attachment.type}::${attachment.size}`;
    const queued = queuedBySignature.get(signature) ?? [];
    queued.push(attachment);
    queuedBySignature.set(signature, queued);
  });

  return next.map((attachment, index) => {
    if (!attachment.type.startsWith('image/')) return attachment;

    const signature = `${attachment.name}::${attachment.type}::${attachment.size}`;
    const matchedQueue = queuedBySignature.get(signature);
    const matchedBySignature = matchedQueue?.shift();
    const matchedByIndex = previous[index];
    const matched =
      matchedBySignature?.previewUrl
        ? matchedBySignature
        : matchedByIndex?.previewUrl
          ? matchedByIndex
          : undefined;

    if (!matched?.previewUrl) return attachment;

    return {
      ...attachment,
      previewUrl: matched.previewUrl,
      file: matched.file ?? attachment.file,
    };
  });
}

function makeAssistantMessage(): ChatMessage {
  return {
    id: uuid(),
    role: 'assistant',
    content: '',
    createdAt: Date.now(),
    isStreaming: true,
    streamEvents: [],
  };
}

function stripTrailingJamo(text: string): string {
  const re = /[\u1100-\u11FF\u3130-\u318F\uA960-\uA97F\uD7B0-\uD7FF]+$/;
  return text.replace(re, '');
}

function normalizeContent(text: string): string {
  try {
    const normalized = text.normalize('NFC');
    return stripTrailingJamo(normalized);
  } catch {
    return stripTrailingJamo(text);
  }
}

function isTeamDeletedSharedGroup(conv: Conversation | null | undefined): boolean {
  return conv?.sharedGroup?.state === 'team_deleted';
}

const SIDEBAR_PAGE_SIZE = 30;
let loadFromBackendInFlight: Promise<void> | null = null;
let initialFastBootstrapDone = false;
let initialFullSyncScheduled = false;
const prefetchingIds = new Set<string>();
const conversationFilesInFlight = new Map<string, Promise<void>>();

function countLoadedWorkspaceItemsForMode(
  workspaceOrder: string[],
  conversations: Record<string, Conversation>,
  mode: WorkspaceMode,
): number {
  let count = 0;
  for (const id of workspaceOrder) {
    if (conversations[id]?.mode === mode) count += 1;
  }
  return count;
}

function scheduleConversationMemorySweep(): void {
  // 최근항목 클릭 핫패스에서는 active 전환/초기 렌더를 우선 처리하고,
  // 메모리 스윕은 다음 틱으로 미뤄 클릭 체감 지연을 줄인다.
  scheduleConversationMemorySweepImpl({
    getState: () => {
      const state = useChatStore.getState();
      return { conversations: state.conversations, activeId: state.activeId };
    },
    setState: (patch) => useChatStore.setState(patch),
  });
}

function isWorkspaceItemNotFoundError(err: unknown): boolean {
  return isNotFoundHttpError(err);
}

async function ensureConversationItemReady(conv: Conversation): Promise<void> {
  let notFoundError: unknown = null;
  try {
    await wsApi.getItem(conv.id);
    return;
  } catch (err) {
    if (isWorkspaceItemAccessDeniedError(err)) {
      throw new Error(i18n.t('common:error.chatAccessRevoked'));
    }
    if (!isWorkspaceItemNotFoundError(err)) {
      throw err;
    }
    notFoundError = err;
  }

  // sharedGroup는 멤버십/팀 상태에 의해 관리되므로 클라이언트에서 임의 재생성하지 않습니다.
  if (conv.sharedGroup) {
    throw (notFoundError instanceof Error
      ? notFoundError
      : new Error(`HTTP 404: {"detail":"${i18n.t('common:error.itemNotFound')}"}`));
  }

  const recreated = await wsApi.createItem(conversationToCreateParams(conv));
  if (!recreated || recreated.status !== 'active') {
    throw new Error(i18n.t('common:error.syncFailed'));
  }
}

function enrichSharedGroupMemberPictures(
  conversationId: string,
  teamId: string,
  get: () => ChatState,
  set: (
    partial: Partial<ChatState> | ((state: ChatState) => Partial<ChatState> | ChatState),
  ) => void,
): void {
  import('./teamStore').then(({ useTeamStore }) => {
    const { currentDetail, loadTeamDetail } = useTeamStore.getState();
    const enrich = (detail: { members: Array<{ user_id: string; picture?: string | null }> }) => {
      const latestConv = get().conversations[conversationId];
      if (!latestConv?.sharedGroup) return;
      const picMap = new Map(detail.members.map((member) => [member.user_id, member.picture ?? null]));
      let changed = false;
      const enriched = latestConv.sharedGroup.members.map((member) => {
        const picture = picMap.get(member.user_id);
        if (picture !== undefined && picture !== member.picture) {
          changed = true;
          return { ...member, picture };
        }
        return member;
      });
      if (!changed) return;
      set((state) => {
        const conversation = state.conversations[conversationId];
        if (!conversation?.sharedGroup) return state;
        return {
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...conversation,
              sharedGroup: { ...conversation.sharedGroup, members: enriched },
            },
          },
        };
      });
    };

    if (currentDetail?.team.team_id === teamId) {
      enrich(currentDetail);
      return;
    }

    loadTeamDetail(teamId).then(() => {
      const detail = useTeamStore.getState().currentDetail;
      if (detail?.team.team_id === teamId) {
        enrich(detail);
      }
    });
  });
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function makeUniqueWorkspaceTitle(
  conversations: Record<string, Conversation>,
  context: {
    mode: WorkspaceMode;
    scope: WorkspaceScope;
    teamId: string | null;
    parentId: string | null;
  },
  desiredTitle: string,
  excludeId?: string,
): string {
  const normalizedTitle = desiredTitle.trim() || desiredTitle || i18n.t('common:chat.newItem');
  const siblingTitles = new Set<string>();

  for (const [id, conv] of Object.entries(conversations)) {
    if (excludeId && id === excludeId) continue;
    if (conv.mode !== context.mode) continue;
    if (conv.workspaceScope !== context.scope) continue;
    const convTeamId = conv.workspaceScope === 'team' ? (conv.workspaceTeamId ?? null) : null;
    const contextTeamId = context.scope === 'team' ? (context.teamId ?? null) : null;
    if (convTeamId !== contextTeamId) continue;
    if ((conv.workspaceParentId ?? null) !== context.parentId) continue;
    siblingTitles.add(conv.title.trim());
  }

  if (!siblingTitles.has(normalizedTitle)) return normalizedTitle;

  const suffixMatch = normalizedTitle.match(/^(.*)\s\((\d+)\)$/);
  const baseTitle = suffixMatch ? suffixMatch[1] : normalizedTitle;
  const escapedBase = escapeRegExp(baseTitle);
  const suffixPattern = new RegExp(`^${escapedBase}\\s\\((\\d+)\\)$`);
  const usedSuffixes = new Set<number>();

  for (const title of siblingTitles) {
    if (title === baseTitle) {
      usedSuffixes.add(0);
      continue;
    }
    const match = title.match(suffixPattern);
    if (match) {
      usedSuffixes.add(Number(match[1]));
    }
  }

  let next = 1;
  while (usedSuffixes.has(next)) next += 1;
  return `${baseTitle} (${next})`;
}

function normalizeStartPageState(
  _mode: WorkspaceMode,
  _state: unknown,
): Conversation['startPageState'] {
  return {};
}

function isSameStartPageState(
  _a: Conversation['startPageState'],
  _b: Conversation['startPageState'],
): boolean {
  return true;
}

function buildNextLiveViewRuntime(
  currentRuntime: ConversationRuntimeState | null,
  liveView: BrowserLiveViewPayload,
  options?: { autoOpen?: boolean; running?: boolean },
): ConversationRuntimeState['liveView'] {
  const previous = currentRuntime?.liveView ?? EMPTY_CONVERSATION_LIVE_VIEW_RUNTIME;
  const available = liveView.available === true;
  const isNewRef = previous.liveViewRef !== liveView.live_view_ref;
  const shouldAutoOpen = options?.autoOpen === true && available && isNewRef;
  return {
    ...previous,
    liveViewRef: liveView.live_view_ref,
    available,
    running: options?.running ?? previous.running,
    open: liveView.state === 'closed' || liveView.state === 'error'
      ? false
      : shouldAutoOpen || (previous.open && previous.liveViewRef === liveView.live_view_ref),
    watchOnly: liveView.watch_only !== false,
    state: liveView.state,
    errorMessage: liveView.error_message ?? null,
  };
}

function buildClearedConversationRuntime(
  runtime: ConversationRuntimeState | null,
): ConversationRuntimeState {
  const liveView = runtime?.liveView ?? EMPTY_CONVERSATION_LIVE_VIEW_RUNTIME;
  return {
    ...(runtime ?? EMPTY_CONVERSATION_RUNTIME),
    abortController: null,
    streamingSessionId: null,
    streamingMessageId: null,
    streamingWorkspaceType: null,
    isUploading: false,
    liveView: {
      ...liveView,
      running: false,
    },
  };
}

function enqueueConversationSettingsSync(
  get: () => ChatState,
  id: string,
): void {
  syncQueue.enqueue({
    key: `conversation:settings:${id}`,
    execute: async () => {
      const latest = get().conversations[id];
      if (!latest) return;
      await wsApi.updateItem(id, {
        settings: buildConversationSettings(latest),
      });
    },
    onSuccess: () => {
      emitAppEvent('workspace:itemsChanged', { source: 'chatStore' });
    },
  });
}

/* localStorage 는 UI 설정(darkMode 등)에만 사용.
 * 워크스페이스 아이템/대화 데이터는 백엔드가 단일 진실 소스(source of truth).
 * 앱 시작 시 빈 상태 → loadFromBackendApi()로 최신 데이터를 적재. */

/* ------------------------------------------------------------------ */
/* Store                                                               */
/* ------------------------------------------------------------------ */

export const useChatStore = create<ChatState>((set, get) => ({
  conversations: {},
  conversationOrder: [],
  workspaceOrder: [],
  activeId: null,
  /* 모바일(<=768px)에서는 사이드바가 position:fixed 오버레이로 화면을
   * 덮으므로 로그인 직후 기본 접힘으로 시작한다. 데스크톱은 기존대로 열림.
   * persist 대상이 아니라 매 로드시 현재 뷰포트 기준으로 재평가된다. */
  sidebarOpen: !isMobileViewport(),
  darkMode: useUIStore.getState().darkMode,
  /* Initial mode is intentionally the B2C-neutral default 'agent'.
   * The host syncs the real mode from the current URL via
   * `useRouteSync` on mount, so this only affects the first render
   * tick. Keeping the default host-agnostic avoids stores depending
   * on the route-config module (which varies per tenant). */
  activeMode: 'agent',
  toasts: useUIStore.getState().toasts,
  conversationRuntimes: {},
  isUploading: false,
  previewFile: null,
  previewFullscreen: false,
  narrowPreviewView: 'preview',
  isNarrowLayout: false,
  loadingConversationId: null,
  totalItemCount: 0,
  totalItemCountByMode: {},
  loadingMoreItems: false,
  workspaceHydrated: false,
  sessionFiles: [],

  /* ---------- conversations ---------- */

  createConversation: (opts) => {
    // 스킬 모드는 현재 대화에 한정된 1회성 선택: 새 대화를 시작하면 Auto 복귀
    if (opts?.activate !== false) {
      resetSkillModeToAuto();
    }
    const mode = opts?.mode ?? get().activeMode;
    const draft = makeConversation(opts?.title ?? '', mode, {
      scope: opts?.scope ?? 'personal',
      kind: opts?.kind ?? 'page',
      teamId: opts?.teamId ?? null,
      parentId: opts?.parentId ?? null,
      startPagePending: opts?.startPagePending ?? false,
      startPageState: opts?.startPageState,
    });
    let convId = draft.id;
    set((s) => {
      const uniqueTitle = makeUniqueWorkspaceTitle(
        s.conversations,
        {
          mode: draft.mode,
          scope: draft.workspaceScope,
          teamId: draft.workspaceTeamId,
          parentId: draft.workspaceParentId,
        },
        draft.title,
      );
      const conv = { ...draft, title: uniqueTitle };
      convId = conv.id;
      const conversations = { ...s.conversations, [conv.id]: conv };
      const conversationOrder = [conv.id, ...s.conversationOrder];
      const workspaceOrder = [conv.id, ...s.workspaceOrder.filter((id) => id !== conv.id)];
      return {
        conversations,
        conversationOrder,
        workspaceOrder,
        activeId: opts?.activate === false ? s.activeId : conv.id,
      };
    });

    // SyncQueue: 생성 실패 시 자동 재시도 → 소진 후 롤백
    const createdConv = get().conversations[convId];
    if (createdConv) {
      syncQueue.enqueue({
        key: `create:${convId}`,
        execute: async () => { await wsApi.createItem(conversationToCreateParams(createdConv)); },
        onSuccess: () => {
          emitAppEvent('workspace:itemsChanged', { source: 'chatStore' });
        },
        onFailure: () => {
          set((s) => {
            const { [convId]: _, ...rest } = s.conversations;
            const conversations = rest;
            const conversationOrder = s.conversationOrder.filter((i) => i !== convId);
            const workspaceOrder = s.workspaceOrder.filter((i) => i !== convId);
            const activeId = s.activeId === convId ? (conversationOrder[0] ?? null) : s.activeId;
            return { conversations, conversationOrder, workspaceOrder, activeId };
          });
          get().addToast({ type: 'error', title: i18n.t('common:toast.createFail'), description: i18n.t('common:error.networkRollback') });
        },
      });
    }
    return draft.id;
  },

  deleteConversation: (id, options) => {
    const state = get();
    const currentConversations = state.conversations;
    const target = currentConversations[id];
    const shouldCascade = options?.cascade === true;
    const descendantIds = shouldCascade ? collectDescendantConversationIds(currentConversations, id) : [];
    const idsToRemove = new Set([id, ...descendantIds]);

    /* Mark `target` as used so the now-slimmer delete path keeps its
     * optimistic-rollback hook point. (Phase: PPT/Image cleanup — the
     * image-folder last-one guard moved out with the workspace.) */
    void target;

    // Optimistic UI: 즉시 반영 후 실패 시 롤백
    const backup = {
      conversations: { ...state.conversations },
      conversationOrder: [...state.conversationOrder],
      workspaceOrder: [...state.workspaceOrder],
      activeId: state.activeId,
    };

    set((s) => {
      const conversations = { ...s.conversations };
      for (const removeId of idsToRemove) {
        delete conversations[removeId];
      }
      if (!shouldCascade) {
        for (const [convId, conv] of Object.entries(conversations)) {
          if (conv.workspaceParentId === id) {
            conversations[convId] = { ...conv, workspaceParentId: null };
          }
        }
      }
      const conversationOrder = s.conversationOrder.filter((i) => !idsToRemove.has(i));
      const workspaceOrder = s.workspaceOrder.filter((i) => !idsToRemove.has(i));
      const activeId = s.activeId && idsToRemove.has(s.activeId) ? (conversationOrder[0] ?? null) : s.activeId;
      return { conversations, conversationOrder, workspaceOrder, activeId };
    });

    syncQueue.enqueue({
      key: `delete:${id}`,
      execute: async () => {
        if (!shouldCascade) {
          await wsApi.deleteItem(id);
          return;
        }
        const depthMap = new Map<string, number>();
        const getDepth = (targetId: string): number => {
          const cached = depthMap.get(targetId);
          if (cached !== undefined) return cached;
          let depth = 0;
          let currentId: string | null = targetId;
          const visited = new Set<string>();
          while (currentId) {
            const current: Conversation | undefined = backup.conversations[currentId];
            const parentId: string | null = current?.workspaceParentId ?? null;
            if (!parentId || visited.has(parentId)) break;
            visited.add(parentId);
            depth += 1;
            currentId = parentId;
          }
          depthMap.set(targetId, depth);
          return depth;
        };
        const orderedIds = [...idsToRemove].sort((a, b) => getDepth(b) - getDepth(a));
        for (const targetId of orderedIds) {
          await wsApi.deleteItem(targetId, true);
        }
      },
      onSuccess: () => {
        emitAppEvent('workspace:itemsChanged', { source: 'chatStore' });
      },
      onFailure: () => {
        set((s) => {
          return {
            conversations: { ...backup.conversations },
            conversationOrder: [...backup.conversationOrder],
            workspaceOrder: [...backup.workspaceOrder],
            activeId: backup.activeId,
          };
        });
        get().addToast({ type: 'error', title: i18n.t('common:toast.deleteFail'), description: i18n.t('common:error.networkRestored') });
      },
    });
  },

  renameConversation: (id, title) => {
    const prevTitle = get().conversations[id]?.title;

    set((s) => {
      const conv = s.conversations[id];
      if (!conv) return s;
      const conversations = { ...s.conversations, [id]: { ...conv, title } };
      return { conversations };
    });

    syncQueue.enqueue({
      key: `rename:${id}`,
      execute: async () => { await wsApi.updateItem(id, { title }); },
      onSuccess: () => {
        emitAppEvent('workspace:itemsChanged', { source: 'chatStore' });
      },
      onFailure: () => {
        if (prevTitle === undefined) return;
        set((s) => {
          const conv = s.conversations[id];
          if (!conv) return s;
          const conversations = { ...s.conversations, [id]: { ...conv, title: prevTitle } };
          return { conversations };
        });
        get().addToast({ type: 'error', title: i18n.t('common:toast.renameFail'), description: i18n.t('common:error.networkRestored') });
      },
    });
  },

  setConversationStartPagePending: (id, pending) => {
    const current = get().conversations[id];
    if (!current || current.startPagePending === pending) return;
    set((s) => {
      const conv = s.conversations[id];
      if (!conv || conv.startPagePending === pending) return s;
      const conversations = {
        ...s.conversations,
        [id]: { ...conv, startPagePending: pending },
      };
      return { conversations };
    });
    enqueueConversationSettingsSync(get, id);
  },

  setConversationStartPageState: (id, patch) => {
    const current = get().conversations[id];
    if (!current) return;
    const nextState = normalizeStartPageState(current.mode, {
      ...current.startPageState,
      ...patch,
    });
    if (isSameStartPageState(current.startPageState, nextState)) return;
    set((s) => {
      const conv = s.conversations[id];
      if (!conv) return s;
      const merged = normalizeStartPageState(conv.mode, {
        ...conv.startPageState,
        ...patch,
      });
      if (isSameStartPageState(conv.startPageState, merged)) return s;
      const conversations = {
        ...s.conversations,
        [id]: { ...conv, startPageState: merged },
      };
      return { conversations };
    });
    enqueueConversationSettingsSync(get, id);
  },

  setActiveConversation: (id, options) => {
    const prev = get();
    if (prev.activeId === id) return;

    // 스킬 모드는 현재 대화에 한정된 1회성 선택: 다른 대화(최근 항목 등)로
    // 전환하면 Auto 복귀
    resetSkillModeToAuto();

    const prevConv = prev.activeId ? prev.conversations[prev.activeId] : null;
    const conv = id ? prev.conversations[id] : null;

    const updates: Partial<ChatState> = {
      activeId: id,
      previewFile: null,
      previewFullscreen: false,
    };
    if (conv?.mode && options?.syncMode !== false) {
      updates.activeMode = conv.mode;
    }

    /* 백엔드에서 로드된 대화이면서 시작 페이지 표시 중이면 해제 */
    if (
      id
      && conv
      && conv.startPagePending
      && (conv.sharedGroup || conv.messages.length === 0)
    ) {
      updates.conversations = {
        ...get().conversations,
        [id]: { ...conv, startPagePending: false },
      };
    }

    updates.loadingConversationId = null;
    set(updates);
    scheduleConversationMemorySweep();
    runConversationSelectionEffects({
      previousConversation: prevConv,
      nextConversationId: id,
      nextConversation: conv,
      getState: get,
      setState: set,
      fetchMessageHistory,
      loadConversationFiles: (conversationId) => get().loadConversationFiles(conversationId),
      isTeamDeletedSharedGroup,
      enrichSharedGroupMembers: (conversationId, teamId) => {
        enrichSharedGroupMemberPictures(conversationId, teamId, get, set);
      },
    });
  },

  moveConversationWorkspace: (id, params) => {
    // Optimistic UI: 이동 전 상태 백업
    const backupConversations = { ...get().conversations };
    const backupWorkspaceOrder = [...get().workspaceOrder];

    set((s) => {
      const conv = s.conversations[id];
      if (!conv) return s;
      if (params.parentId === id) return s;
      if (params.parentId && !s.conversations[params.parentId]) return s;
      if (params.parentId && isDescendantConversation(s.conversations, params.parentId, id)) {
        return s;
      }

      const targetParent = params.parentId ? s.conversations[params.parentId] : null;
      const nextScope: WorkspaceScope = targetParent?.workspaceScope ?? params.scope;
      const nextTeamId = nextScope === 'team'
        ? (targetParent?.workspaceTeamId ?? params.teamId ?? null)
        : null;
      const destinationChanged =
        conv.workspaceParentId !== params.parentId
        || conv.workspaceScope !== nextScope
        || (conv.workspaceTeamId ?? null) !== nextTeamId;
      const nextTitle = destinationChanged
        ? makeUniqueWorkspaceTitle(
          s.conversations,
          {
            mode: conv.mode,
            scope: nextScope,
            teamId: nextTeamId,
            parentId: params.parentId,
          },
          conv.title,
          id,
        )
        : conv.title;

      const descendantIds = collectDescendantConversationIds(s.conversations, id);
      const conversations = { ...s.conversations };

      conversations[id] = {
        ...conv,
        title: nextTitle,
        workspaceParentId: params.parentId,
        workspaceScope: nextScope,
        workspaceTeamId: nextTeamId,
      };

      for (const childId of descendantIds) {
        const child = conversations[childId];
        if (!child) continue;
        conversations[childId] = {
          ...child,
          workspaceScope: nextScope,
          workspaceTeamId: nextTeamId,
        };
      }

      const baseOrder = s.workspaceOrder.filter((cid) => cid !== id);
      const insertBeforeId = params.insertBeforeId && baseOrder.includes(params.insertBeforeId)
        ? params.insertBeforeId
        : null;
      const insertAfterId = params.insertAfterId && baseOrder.includes(params.insertAfterId)
        ? params.insertAfterId
        : null;

      const workspaceOrder = [...baseOrder];
      if (insertBeforeId) {
        const insertIndex = workspaceOrder.indexOf(insertBeforeId);
        workspaceOrder.splice(insertIndex, 0, id);
      } else if (insertAfterId) {
        const anchorIndex = workspaceOrder.indexOf(insertAfterId);
        workspaceOrder.splice(anchorIndex + 1, 0, id);
      } else {
        workspaceOrder.unshift(id);
      }
      return { conversations, workspaceOrder };
    });

    const movedConv = get().conversations[id];
    const nextScope: WorkspaceScope = movedConv?.workspaceScope ?? params.scope;
    const nextTeamId = movedConv?.workspaceTeamId ?? params.teamId ?? null;

    syncQueue.enqueue({
      key: `move:${id}`,
      execute: async () => {
        await wsApi.moveItem(id, { parent_id: params.parentId, scope: nextScope, team_id: nextTeamId });
      },
      onSuccess: () => {
        emitAppEvent('workspace:itemsChanged', { source: 'chatStore' });
      },
      onFailure: () => {
        set(() => {
          return { conversations: backupConversations, workspaceOrder: backupWorkspaceOrder };
        });
        get().addToast({ type: 'error', title: i18n.t('common:toast.moveFail'), description: i18n.t('common:error.networkRestored') });
      },
    });
  },

  /* ---------- ui ---------- */

  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  setSidebarOpen: (v) => set({ sidebarOpen: v }),
  toggleDarkMode: () => useUIStore.getState().toggleDarkMode(),
  setActiveMode: (mode) => set({ activeMode: mode }),

  /* ---------- file preview ---------- */

  setPreviewFile: (file) => set((state) => {
    const nextState: Partial<ChatState> = {
      previewFile: file,
      ...(file === null
        ? { previewFullscreen: false }
        : { narrowPreviewView: 'preview' }),
    };

    if (file !== null && state.activeId) {
      const runtime = getConversationRuntime(state.conversationRuntimes, state.activeId);
      const conversation = state.conversations[state.activeId];
      const currentLiveView = runtime?.liveView ?? (
        conversation?.liveView?.liveViewRef
          ? {
              ...EMPTY_CONVERSATION_LIVE_VIEW_RUNTIME,
              liveViewRef: conversation.liveView.liveViewRef,
              available: conversation.liveView.available,
              watchOnly: conversation.liveView.watchOnly,
              state: conversation.liveView.state,
              errorMessage: conversation.liveView.errorMessage ?? null,
            }
          : null
      );

      if (currentLiveView?.open) {
        nextState.conversationRuntimes = patchConversationRuntimeMap(
          state.conversationRuntimes,
          state.activeId,
          { liveView: { ...currentLiveView, open: false } },
        );
      }
    }

    return nextState;
  }),
  setNarrowPreviewView: (v) =>
    set((s) => (s.narrowPreviewView === v ? s : { narrowPreviewView: v })),
  setNarrowLayout: (v) =>
    set((s) => (s.isNarrowLayout === v ? s : { isNarrowLayout: v })),
  setPreviewFullscreen: (v) => set({ previewFullscreen: v }),
  togglePreviewFullscreen: () => set((s) => ({ previewFullscreen: !s.previewFullscreen })),

  /* ---------- session files ---------- */

  loadSessionFiles: async (sessionId) => {
    try {
      const { files } = await wsApi.listFiles({ session_id: sessionId, limit: 200 });
      set({ sessionFiles: files });
    } catch {
      set({ sessionFiles: [] });
    }
  },

  loadConversationFiles: async (conversationId) => {
    const existing = conversationFilesInFlight.get(conversationId);
    if (existing) {
      await existing;
      return;
    }

    const run = (async () => {
      const conv = get().conversations[conversationId];
      if (!conv) {
        if (get().activeId === conversationId) {
          set({ sessionFiles: [] });
        }
        return;
      }

      const applyFiles = (files: WorkspaceFile[]) => {
        if (get().activeId !== conversationId) return;
        set({ sessionFiles: files });
      };

      try {
        if (conv.sharedGroup) {
          const { files } = await wsApi.listFiles({ item_id: conv.id, limit: 500 });
          applyFiles(files);
          return;
        }
        if (conv.sessionId) {
          const { files } = await wsApi.listFiles({ session_id: conv.sessionId, limit: 200 });
          applyFiles(files);
          return;
        }
        applyFiles([]);
      } catch {
        if (get().activeId === conversationId) {
          set({ sessionFiles: [] });
        }
      }
    })();

    conversationFilesInFlight.set(conversationId, run);
    try {
      await run;
    } finally {
      if (conversationFilesInFlight.get(conversationId) === run) {
        conversationFilesInFlight.delete(conversationId);
      }
    }
  },

  removeSessionFile: async (fileId) => {
    set((s) => ({
      sessionFiles: s.sessionFiles.filter((f) => f.file_id !== fileId),
      previewFile: s.previewFile?.id === fileId ? null : s.previewFile,
    }));
    try {
      await wsApi.deleteFile(fileId);
    } catch { /* soft-delete 실패 시 UI 상태만 유지 */ }
  },

  refreshSessionFiles: async () => {
    const conv = get().activeConversation();
    if (conv?.id) {
      await get().loadConversationFiles(conv.id);
    }
  },

  /* ---------- toast ---------- */

  addToast: (t) => useUIStore.getState().addToast(t),

  removeToast: (id) => useUIStore.getState().removeToast(id),

  /* ---------- backend sync ---------- */

  loadFromBackendApi: async () => {
    if (loadFromBackendInFlight) return loadFromBackendInFlight;

    loadFromBackendInFlight = (async () => {
      const applyLoadResult = (result: {
        conversations: Record<string, Conversation>;
        order: string[];
        workspaceOrder: string[];
        count: number;
      }) => {
        set((s) => mergeConversationBatch(s, result.conversations, {
          order: result.order,
          workspaceOrder: result.workspaceOrder,
          totalCount: result.count,
          workspaceHydrated: true,
        }));
      };

      const scheduleInitialFullSync = () => {
        if (initialFullSyncScheduled || typeof window === 'undefined') return;
        initialFullSyncScheduled = true;
        window.setTimeout(() => {
          initialFullSyncScheduled = false;
          void get().loadFromBackendApi();
        }, 0);
      };

      // 로그인 직후 첫 부트스트랩은 recent 소량만 먼저 적재해서 첫 화면 응답성을 확보한다.
      const shouldUseFastBootstrap =
        !initialFastBootstrapDone
        && Object.keys(get().conversations).length === 0;

      if (shouldUseFastBootstrap) {
        const cachedResult = readWorkspaceBootstrapCache();
        if (cachedResult) {
          applyLoadResult(cachedResult);
          initialFastBootstrapDone = true;
          scheduleInitialFullSync();
          return;
        }

        const fastMode = get().activeMode === 'agent' ? 'agent' : undefined;
        const fastResult = await loadRecentFromBackend(FAST_BOOTSTRAP_RECENT_LIMIT, fastMode);
        if (fastResult) {
          applyLoadResult(fastResult);
          writeWorkspaceBootstrapCache(fastResult);
          initialFastBootstrapDone = true;
          // full list 동기화는 다음 tick에서 백그라운드로 이어서 수행한다.
          scheduleInitialFullSync();
          return;
        }
      }

      const result = await loadFromBackend({ limit: SIDEBAR_PAGE_SIZE });
      if (!result) return;
      applyLoadResult(result);
      writeWorkspaceBootstrapCache(result);
      initialFastBootstrapDone = true;
    })().finally(() => {
      loadFromBackendInFlight = null;
    });

    return loadFromBackendInFlight;
  },

  loadMoreItems: async (options) => {
    const targetMode = options?.mode;
    const { loadingMoreItems, workspaceOrder, totalItemCount, totalItemCountByMode, conversations } = get();
    if (loadingMoreItems) return;
    const loaded = targetMode
      ? countLoadedWorkspaceItemsForMode(workspaceOrder, conversations, targetMode)
      : workspaceOrder.length;
    const knownTotal = targetMode ? totalItemCountByMode[targetMode] : totalItemCount;
    if (typeof knownTotal === 'number' && loaded >= knownTotal) return;

    set({ loadingMoreItems: true });
    try {
      const result = await loadFromBackend({ limit: SIDEBAR_PAGE_SIZE, skip: loaded, mode: targetMode });
      if (!result) {
        set({ loadingMoreItems: false });
        return;
      }

      set((s) => ({
        ...mergeConversationBatch(s, result.conversations, {
          order: result.order.length > 0 ? [...s.conversationOrder, ...result.order] : s.conversationOrder,
          workspaceOrder: result.workspaceOrder.length > 0
            ? [...s.workspaceOrder, ...result.workspaceOrder]
            : s.workspaceOrder,
          totalCount: targetMode ? s.totalItemCount : result.count,
          modeCount: targetMode ? { mode: targetMode, count: result.count } : undefined,
        }),
        loadingMoreItems: false,
      }));
    } catch {
      set({ loadingMoreItems: false });
    }
  },

  prefetchMessages: (id) => {
    const conv = get().conversations[id];
    if (!conv || conv.historyLoaded) return;
    if (prefetchingIds.has(id)) return;
    prefetchingIds.add(id);

    fetchMessageHistory(id).then((msgs) => {
      if (msgs.length > 0) {
        persistMessagesToCache(id, msgs);
      }
    }).finally(() => {
      prefetchingIds.delete(id);
    });
  },

  applyRemoteEvent: (evt) => {
    const { type, item_id, data } = evt;
    if (!item_id) return;
    const currentUserId = useUserStore.getState().user?.userId ?? '';

    set((s) => {
      const conversations = { ...s.conversations };
      let conversationOrder = [...s.conversationOrder];
      let workspaceOrder = [...s.workspaceOrder];
      let activeId = s.activeId;

      const removeConversation = () => {
        if (!conversations[item_id]) return;
        delete conversations[item_id];
        conversationOrder = conversationOrder.filter((id) => id !== item_id);
        workspaceOrder = workspaceOrder.filter((id) => id !== item_id);
        for (const [cid, conv] of Object.entries(conversations)) {
          if (conv.workspaceParentId === item_id) {
            conversations[cid] = { ...conv, workspaceParentId: null };
          }
        }
        if (activeId === item_id) {
          activeId = conversationOrder[0] ?? null;
        }
      };

      if (type === 'item_deleted') {
        if (!conversations[item_id]) return s;
        removeConversation();
      } else if (
        (type === 'item_created' || type === 'item_updated' || type === 'item_moved')
        && data && typeof data === 'object'
      ) {
        const item = data as import('@auw/workspace-utils').WorkspaceItem;
        const existing = conversations[item_id];
        const conv = itemToConversation(item);
        const itemStatus = item.status ?? 'active';
        const removedByStatus = itemStatus !== 'active';
        const removedByMembership =
          !!conv.sharedGroup
          && !!currentUserId
          && !conv.sharedGroup.member_ids.includes(currentUserId);

        if (removedByStatus || removedByMembership) {
          if (!existing) return s;
          removeConversation();
          return { conversations, conversationOrder, workspaceOrder, activeId };
        }

        conversations[item_id] = mergeConversationEntity(existing, conv);

        // 새 아이템이거나, 공유로 인해 로컬에 없던 아이템이면 order에 추가
        const isNewLocally = !existing;
        if (type === 'item_created' || (isNewLocally && (type === 'item_updated' || type === 'item_moved'))) {
          if (!workspaceOrder.includes(item_id)) {
            workspaceOrder = [item_id, ...workspaceOrder];
          }
          if (conv.workspaceKind === 'page' && !conversationOrder.includes(item_id)) {
            conversationOrder = [item_id, ...conversationOrder];
          }
        }
      } else {
        return s;
      }

      const memoryManaged = applyConversationMemoryPolicy(conversations, activeId);
      return { conversations: memoryManaged, conversationOrder, workspaceOrder, activeId };
    });
  },

  removeConversationLocal: (id) => {
    set((s) => {
      if (!s.conversations[id]) return s;
      const { [id]: _, ...rest } = s.conversations;
      const conversations = { ...rest };
      for (const [cid, conv] of Object.entries(conversations)) {
        if (conv.workspaceParentId === id) {
          conversations[cid] = { ...conv, workspaceParentId: null };
        }
      }
      const conversationOrder = s.conversationOrder.filter((i) => i !== id);
      const workspaceOrder = s.workspaceOrder.filter((i) => i !== id);
      const activeId = s.activeId === id ? (conversationOrder[0] ?? null) : s.activeId;
      return { conversations, conversationOrder, workspaceOrder, activeId };
    });
  },

  /* ---------- messaging ---------- */

  sendMessage: (content, attachments, options) => {
    const state = get();
    let {
      conversationId: convId,
      requestedMode,
      previewConversation: convPreview,
      shouldInvokeAgentPreview,
      billingContext,
    } = resolveSendMessageTarget({
      activeId: state.activeId,
      activeMode: state.activeMode,
      conversations: state.conversations,
      createConversation: state.createConversation,
      content,
      options,
    });

    // 토큰이 소진된 상태에서는 요청/동기화를 시작하지 않습니다.
    if (
      shouldInvokeAgentPreview
      && billingContext.remainingTokens !== null
      && billingContext.remainingTokens <= 0
    ) {
      handleTokensExhausted(billingContext.scope);
      return;
    }

    const modeState = get();
    const modeConversation = convId ? modeState.conversations[convId] ?? null : null;
    const messageWorkspaceMode =
      options?.modeOverride
      ?? modeConversation?.mode
      ?? (
        modeState.activeMode === 'widget' || modeState.activeMode === 'feed' || modeState.activeMode === 'tools'
          ? 'agent'
          : modeState.activeMode
      )
      ?? 'agent';
    /* 공유받은 대화에서는 새 대화를 만들지 않음 — 기존 대화를 사용 */

    /* 그룹채팅이면 현재 유저 정보를 메시지에 포함 */
    const conv0 = get().conversations[convId];
    if (isTeamDeletedSharedGroup(conv0)) {
      get().addToast({
        type: 'warning',
        title: i18n.t('common:toast.messageSendFail'),
        description: i18n.t('common:error.teamSpaceDeleted'),
      });
      return;
    }
    let sender: { id: string; name: string | null } | null = null;
    if (conv0?.sharedGroup) {
      const u = useUserStore.getState().user;
      if (u) sender = { id: u.userId, name: u.name || u.email };
    }
    const shouldInvokeAgent =
      !conv0?.sharedGroup
      || !!options?.forceInvokeAgent
      || GROUP_AGENT_MENTION_RE.test(content);

    const existingRuntime = getConversationRuntime(get().conversationRuntimes, convId);
    if (isConversationRuntimeBusy(existingRuntime)) {
      get().addToast({
        type: 'warning',
        title: i18n.t('common:error.operationInProgress'),
        description: i18n.t('common:error.waitForCompletion'),
      });
      return;
    }

    const reboMode = options?.reboMode ?? 'auto';
    const mentionedUserIds = (
      conv0?.sharedGroup && Array.isArray(options?.mentionedUserIds) && options.mentionedUserIds.length > 0
    )
      ? options.mentionedUserIds
      : null;
    const userMsgMetadata: Record<string, unknown> = {
      ...(reboMode !== 'auto' ? { reboMode } : {}),
      ...(mentionedUserIds ? { mentioned_user_ids: mentionedUserIds } : {}),
    };
    const userMsg = makeUserMessage(content, attachments, sender,
      Object.keys(userMsgMetadata).length > 0 ? userMsgMetadata : undefined,
    );
    const assistantMsg = shouldInvokeAgent ? makeAssistantMessage() : null;

    /* 대화 제목 자동 설정 (첫 메시지에서)
     *
     * doc-agent 는 "빈 템플릿으로 생성한 문서" 에서만 자동 제목을 만든다.
     * 사용자가 업로드한 문서는 파일명을 제목으로 유지해야 하므로,
     * requestMetadata.doc_agent.allow_title_autogen 이 false 면 제목을 바꾸지
     * 않는다(fallbackTitle = conv.title → 아래 fallback 동기화·LLM 추천 블록이
     * `fallbackTitle !== conv.title` 가드로 모두 skip 됨). 통합(일반) 에이전트는
     * doc_agent 메타가 없으므로 기존 동작을 그대로 유지한다. */
    const conv = get().conversations[convId]!;
    const isFirst = conv.messages.length === 0;
    const docAgentReqMeta =
      options?.requestMetadata && typeof options.requestMetadata === 'object'
        ? (options.requestMetadata as Record<string, unknown>).doc_agent
        : undefined;
    const titleAutoEnabled =
      docAgentReqMeta && typeof docAgentReqMeta === 'object'
        ? (docAgentReqMeta as Record<string, unknown>).allow_title_autogen === true
        : true;
    const fallbackTitle =
      isFirst && titleAutoEnabled
        ? buildFirstMessageFallbackTitle(content)
        : conv.title;

    set((s) => {
      const c = s.conversations[convId!]!;
      const updated: Conversation = {
        ...c,
        title: fallbackTitle,
        startPagePending: false,
        messages: assistantMsg
          ? [...c.messages, userMsg, assistantMsg]
          : [...c.messages, userMsg],
        updatedAt: Date.now(),
      };
      const conversations = { ...s.conversations, [convId!]: updated };
      const conversationOrder = [convId!, ...s.conversationOrder.filter((i) => i !== convId)];
      return { conversations, conversationOrder };
    });

    const patchConversationRuntime = (patch: Partial<ConversationRuntimeState>) => {
      set((s) => ({
        conversationRuntimes: patchConversationRuntimeMap(
          s.conversationRuntimes,
          convId!,
          patch,
        ),
      }));
    };

    const clearConversationRuntime = () => {
      set((s) => ({
        conversationRuntimes: patchConversationRuntimeMap(
          s.conversationRuntimes,
          convId!,
          buildClearedConversationRuntime(
            getConversationRuntime(s.conversationRuntimes, convId!),
          ),
        ),
      }));
    };

    /* 사용자 메시지 동기화 큐 (첨부가 있으면 업로드 이후 1회 실행) */
    let userMessageSyncQueued = false;
    const enqueueUserMessageSync = () => {
      if (userMessageSyncQueued) return;
      userMessageSyncQueued = true;
      syncQueue.enqueue({
        key: `message:user:${convId}:${userMsg.id}`,
        execute: async () => {
          const { syncSaveMessage } = await import('@auw/api-client');
          const saved = await syncSaveMessage(convId!, userMsg);
          if (!saved) throw new Error('user message sync failed');
          const canonicalMessageId =
            saved.message_id && typeof saved.message_id === 'string'
              ? saved.message_id
              : userMsg.id;
          if (canonicalMessageId !== userMsg.id) {
            const previousMessageId = userMsg.id;
            userMsg.id = canonicalMessageId;
            set((prev) => {
              const currentConv = prev.conversations[convId!];
              if (!currentConv) return prev;
              let touched = false;
              const nextMessages = currentConv.messages.map((m) => {
                if (m.id !== previousMessageId) return m;
                touched = true;
                return { ...m, id: canonicalMessageId };
              });
              if (!touched) return prev;
              return {
                conversations: {
                  ...prev.conversations,
                  [convId!]: {
                    ...currentConv,
                    messages: nextMessages,
                    updatedAt: Date.now(),
                  },
                },
              };
            });
          }
        },
      });
    };
    if (!attachments || attachments.length === 0) {
      enqueueUserMessageSync();
    }

    /* 첫 메시지에서 제목이 바뀌었으면 백엔드에도 동기화 */
    if (isFirst && fallbackTitle !== conv.title) {
      syncQueue.enqueue({
        key: `title:${convId}`,
        execute: async () => { await wsApi.updateItem(convId!, { title: fallbackTitle }); },
        onSuccess: () => {
          emitAppEvent('workspace:itemsChanged', { source: 'chatStore' });
        },
      });
    }

    /* 스트리밍 시작 */
    const AGENT_STREAM_TIMEOUT_MS = 30 * 60 * 1000; // 30분
    const abortController = shouldInvokeAgent ? new AbortController() : null;
    let streamTimeoutId: ReturnType<typeof setTimeout> | null = null;
    if (abortController) {
      patchConversationRuntime({ abortController });
      streamTimeoutId = setTimeout(() => {
        streamTimeoutId = null;
        abortController.abort();
      }, AGENT_STREAM_TIMEOUT_MS);
    }

    const assistantId = assistantMsg?.id ?? '';
    let messageId = uuid();

    (async () => {
      /* ── 토큰 버퍼링: rAF 배치 처리로 부드러운 스트리밍 ──
       *  FinalAnswer 토큰을 버퍼에 모아 requestAnimationFrame 단위로 한 번에 플러시.
       *  매 토큰마다 set()을 호출하지 않아 메인 스레드 차단 없이 부드러운 렌더링이 가능함.
       */
      let tokenBuf = '';
      let rafHandle = 0;

      const flushTokenBuffer = () => {
        rafHandle = 0;
        if (!tokenBuf) return;
        const chunk = tokenBuf;
        tokenBuf = '';
        const st = get();
        const cv = st.conversations[convId!];
        if (!cv) return;
        const ms = [...cv.messages];
        const i = ms.findIndex((m) => m.id === assistantId);
        if (i === -1) return;
        ms[i] = { ...ms[i], content: normalizeContent(ms[i].content + chunk) };
        set({
          conversations: {
            ...st.conversations,
            [convId!]: { ...cv, messages: ms, updatedAt: Date.now() },
          },
        });
      };

      let thinkingBuf = '';
      let thinkingRafHandle = 0;
      let isInThinkingPhase = false;

      const flushThinkingBuffer = () => {
        thinkingRafHandle = 0;
        if (!thinkingBuf) return;
        const chunk = thinkingBuf;
        thinkingBuf = '';
        const st = get();
        const cv = st.conversations[convId!];
        if (!cv) return;
        const ms = [...cv.messages];
        const i = ms.findIndex((m) => m.id === assistantId);
        if (i === -1) return;
        ms[i] = {
          ...ms[i],
          thinkingContent: (ms[i].thinkingContent || '') + chunk,
          isThinking: isInThinkingPhase,
        };
        set({
          conversations: {
            ...st.conversations,
            [convId!]: { ...cv, messages: ms, updatedAt: Date.now() },
          },
        });
      };

      const seenLiveUsageSeq = new Set<number>();
      let lastAnswerChunkSignature: string | null = null;
      let lastThinkingChunkSignature: string | null = null;

      const buildLiveChunkSignature = (
        channel: 'answer' | 'thinking',
        data: AgentInProgressData,
      ): string | null => {
        if (typeof data.detail !== 'string') return null;
        return JSON.stringify({
          channel,
          event: data.event ?? null,
          actorType: data.actor?.type ?? null,
          actorName: data.actor?.name ?? null,
          detail: data.detail,
        });
      };

      const shouldSkipLiveChunk = (
        channel: 'answer' | 'thinking',
        data: AgentInProgressData,
      ): boolean => {
        if (typeof data.usage_seq === 'number') {
          if (seenLiveUsageSeq.has(data.usage_seq)) return true;
          seenLiveUsageSeq.add(data.usage_seq);
          return false;
        }

        const signature = buildLiveChunkSignature(channel, data);
        if (!signature) return false;

        if (channel === 'answer') {
          if (signature === lastAnswerChunkSignature) return true;
          lastAnswerChunkSignature = signature;
          return false;
        }

        if (signature === lastThinkingChunkSignature) return true;
        lastThinkingChunkSignature = signature;
        return false;
      };

      const resetLiveChunkDedup = () => {
        seenLiveUsageSeq.clear();
        lastAnswerChunkSignature = null;
        lastThinkingChunkSignature = null;
      };

      const sleep = (ms: number) => new Promise<void>((resolve) => {
        window.setTimeout(resolve, ms);
      });

      const refreshConversationFilesWithRetry = async (
        conversationId: string,
        expectedFileIds: string[] = [],
      ) => {
        const targetIds = Array.from(
          new Set(
            expectedFileIds.filter((id): id is string => typeof id === 'string' && id.trim().length > 0),
          ),
        );
        // 백엔드에서 completed 직전에 파일 메타를 반영하도록 개선했으므로
        // 폴링은 최소 fallback 용도로만 짧게 유지
        const retryDelaysMs = [180, 520];

        for (let i = 0; i < retryDelaysMs.length; i += 1) {
          const stateNow = get();
          if (stateNow.activeId !== conversationId) return;
          await stateNow.loadConversationFiles(conversationId);

          if (targetIds.length === 0) return;
          const latestState = get();
          const seenIds = new Set(latestState.sessionFiles.map((f) => f.file_id));
          const allPresent = targetIds.every((id) => seenIds.has(id));
          if (allPresent) return;

          await sleep(retryDelaysMs[i]);
        }
      };

      const mergeOptimisticOutputFiles = (
        conversationId: string,
        outputFiles: NonNullable<AgentCompletedData['output_files']>,
        resolvedSessionId?: string | null,
      ) => {
        if (!Array.isArray(outputFiles) || outputFiles.length === 0) return;
        const currentUserId = useUserStore.getState().user?.userId || '';
        const nowIso = new Date().toISOString();

        set((prev) => {
          if (prev.activeId !== conversationId) return prev;
          const byId = new Map(prev.sessionFiles.map((f) => [f.file_id, f]));
          let touched = false;

          for (const f of outputFiles) {
            const fileId = typeof f.file_id === 'string' ? f.file_id.trim() : '';
            if (!fileId || byId.has(fileId)) continue;
            byId.set(fileId, {
              file_id: fileId,
              user_id: currentUserId,
              original_name: f.name || fileId,
              stored_name: f.name || fileId,
              mime_type: f.mime_type || 'application/octet-stream',
              size_bytes: typeof f.size === 'number' ? f.size : 0,
              storage_path: '',
              source_type: 'agent_output',
              thumbnail_path: null,
              metadata: {
                optimistic: true,
                ...(resolvedSessionId ? { session_id: resolvedSessionId } : {}),
              },
              status: 'active',
              created_at: f.created_at || nowIso,
            });
            touched = true;
          }

          if (!touched) return prev;
          return { sessionFiles: Array.from(byId.values()) };
        });
      };

      try {
        // 첫 메시지 전송 시에는 아이템 생성 동기화 레이스를 방지하기 위해 존재를 보장합니다.
        if (isFirst) {
          const latestConv = get().conversations[convId!];
          if (latestConv) {
            await ensureConversationItemReady(latestConv);
          }

          if (fallbackTitle !== conv.title) {
            /* doc-agent 는 작업 유형(생성/편집/QA)과 문서 핵심 내용을 반영한
             * 제목을 만들기 위해 문서 컨텍스트를 함께 보낸다.
             * requestMetadata.doc_agent 가 있을 때만 doc_context 를 채우며,
             * 통합(일반) 에이전트는 기존처럼 content 만 보낸다. */
            const docAgentMeta =
              options?.requestMetadata && typeof options.requestMetadata === 'object'
                ? (options.requestMetadata as Record<string, unknown>).doc_agent
                : undefined;
            let docContext:
              | { format?: string; is_empty?: boolean; summary?: string }
              | undefined;
            if (docAgentMeta && typeof docAgentMeta === 'object') {
              const meta = docAgentMeta as Record<string, unknown>;
              docContext = {};
              if (typeof meta.format === 'string') docContext.format = meta.format;
              if (typeof meta.is_empty === 'boolean') docContext.is_empty = meta.is_empty;
              if (typeof meta.content_summary === 'string') {
                docContext.summary = meta.content_summary;
              }
            }
            void (async () => {
              try {
                const suggested = await wsApi.suggestItemTitle(
                  convId!,
                  docContext ? { content, doc_context: docContext } : { content },
                );
                const suggestedTitle = typeof suggested.title === 'string' ? suggested.title.trim() : '';
                if (!suggestedTitle || suggestedTitle === fallbackTitle) return;

                let applied = false;
                set((s) => {
                  const current = s.conversations[convId!];
                  if (!current || current.title !== fallbackTitle) return s;
                  applied = true;
                  return {
                    conversations: {
                      ...s.conversations,
                      [convId!]: { ...current, title: suggestedTitle, updatedAt: Date.now() },
                    },
                  };
                });

                if (!applied) return;
                syncQueue.enqueue({
                  key: `title:${convId}`,
                  execute: async () => { await wsApi.updateItem(convId!, { title: suggestedTitle }); },
                  onSuccess: () => {
                    emitAppEvent('workspace:itemsChanged', { source: 'chatStore' });
                  },
                });
              } catch {
                // 제목 추천 실패 시 기존 fallback 제목 유지
              }
            })();
          }
        }

        /* 파일 첨부가 있으면 먼저 업로드 */
        let uploadedPaths: string[] | undefined;
        let uploadResult: Awaited<ReturnType<typeof uploadFiles>> | undefined;
        let sessionId = conv.sessionId;

        if (attachments && attachments.length > 0) {
          const localAttachments = attachments.filter(
            (a) => a.file && !a.cloudSource && !a.mailSource && !a.collaborationSource && !a.messengerSource,
          );
          const cloudAttachments = attachments.filter((a) => a.cloudSource && a.driveFileId);
          const collaborationAttachments = attachments.filter(
            (a) => !!a.collaborationSource && !!a.remoteUrl,
          );
          const mailAttachments = attachments.filter((a) => a.mailSource && a.mailAttachmentId);
          const mailContentAttachments = attachments.filter((a) => a.mailSource && a.file && !a.mailAttachmentId);
          const messengerAttachments = attachments.filter(
            (a) => !!a.messengerSource && !!a.messengerFileId,
          );
          patchConversationRuntime({ isUploading: true });
          try {
            const mapWithConcurrency = async <T,>(
              items: T[],
              worker: (item: T) => Promise<File>,
              concurrency: number,
            ): Promise<File[]> => {
              if (items.length === 0) return [];
              const results: File[] = new Array(items.length);
              let index = 0;
              const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
                while (true) {
                  const current = index;
                  index += 1;
                  if (current >= items.length) break;
                  results[current] = await worker(items[current]);
                }
              });
              await Promise.all(workers);
              return results;
            };
            const maxConcurrentRemoteDownloads = 4;

            let cloudFiles: File[] = [];
            if (cloudAttachments.length > 0) {
              cloudFiles = await mapWithConcurrency(
                cloudAttachments,
                (a) => downloadCloudFile(a.cloudSource!, a.driveFileId!, a.name),
                maxConcurrentRemoteDownloads,
              );
            }
            let collaborationFiles: File[] = [];
            if (collaborationAttachments.length > 0) {
              collaborationFiles = await mapWithConcurrency(
                collaborationAttachments,
                (a) => downloadCollaborationAttachment(a.remoteUrl!, a.name),
                maxConcurrentRemoteDownloads,
              );
            }
            let mailFiles: File[] = [];
            if (mailAttachments.length > 0) {
              mailFiles = await mapWithConcurrency(
                mailAttachments,
                (a) => downloadMailAttachment(a.mailSource!, a.mailMessageId!, a.mailAttachmentId!, a.name),
                maxConcurrentRemoteDownloads,
              );
            }
            let messengerFiles: File[] = [];
            if (messengerAttachments.length > 0) {
              messengerFiles = await mapWithConcurrency(
                messengerAttachments,
                (a) => downloadMessengerAttachment(a.messengerSource!, a.messengerFileId!, a.name),
                maxConcurrentRemoteDownloads,
              );
            }
            const allFiles = [
              ...localAttachments.map((a) => a.file!),
              ...cloudFiles,
              ...mailFiles,
              ...collaborationFiles,
              ...messengerFiles,
              ...mailContentAttachments.map((a) => a.file!),
            ];
            const result = await uploadFiles(
              allFiles,
              undefined,
              sessionId,
              messageId,
              messageWorkspaceMode,
              convId!,
            );
            uploadedPaths = result.uploaded;
            uploadResult = result;
            if (Array.isArray(result.uploaded_files) && result.uploaded_files.length > 0) {
              const remoteAttachments = mergeUploadedAttachmentPreviews(
                userMsg.attachments,
                result.uploaded_files
                .map((f) => buildRemoteAttachment({
                  file_id: f.file_id,
                  name: f.name,
                  mime_type: f.mime_type,
                  size: f.size,
                }))
                .filter((v): v is FileAttachment => !!v),
              );
              if (remoteAttachments.length > 0) {
                userMsg.attachments = remoteAttachments;
                set((s) => {
                  const cv = s.conversations[convId!];
                  if (!cv) return s;
                  const messages = [...cv.messages];
                  const userIdx = messages.findIndex((m) => m.id === userMsg.id);
                  if (userIdx === -1) return s;
                  messages[userIdx] = {
                    ...messages[userIdx],
                    attachments: remoteAttachments,
                  };
                  return {
                    conversations: {
                      ...s.conversations,
                      [convId!]: { ...cv, messages, updatedAt: Date.now() },
                    },
                  };
                });
              }
            }
            // 서버에서 반환된 session_id 를 사용 (새 세션이면 자동 생성됨)
            if (result.session_id && result.session_id !== sessionId) {
              const newSid = result.session_id;
              sessionId = newSid;
              set((s) => {
                const cv = s.conversations[convId!];
                if (!cv) return s;
                return { conversations: { ...s.conversations, [convId!]: { ...cv, sessionId: newSid } } };
              });
              syncQueue.enqueue({
                key: `session_id:${convId}`,
                execute: async () => { await wsApi.updateItem(convId!, { session_id: newSid }); },
              });
            } else if (result.session_id) {
              sessionId = result.session_id;
            }
            if (result.message_id) {
              messageId = result.message_id;
            }
            if (result.errors && result.errors.length > 0) {
              const failedNames = result.errors.map((e) => e.file).join(', ');
              get().addToast({
                type: 'warning',
                title: i18n.t('common:toast.partialUploadFail'),
                description: failedNames,
              });
            }
          } catch (uploadErr: unknown) {
            get().addToast({
              type: 'error',
              title: i18n.t('common:error.uploadFailed'),
              description: (uploadErr as Error).message,
            });
          } finally {
            patchConversationRuntime({ isUploading: false });
            void get().loadConversationFiles(convId!);
          }
        }

        if (attachments && attachments.length > 0) {
          enqueueUserMessageSync();
        }

        if (!shouldInvokeAgent || !assistantMsg || !abortController) {
          return;
        }

        // ``workspaceTypeOverride`` 가 있으면 백엔드 ``workspace_type`` 만
        // 포맷별 엔드포인트로 바꾼다 (UI 대화 모드는 messageWorkspaceMode 유지).
        const currentMode = options?.workspaceTypeOverride ?? messageWorkspaceMode;
        const latestConv = get().conversations[convId!];

        // history는 백엔드 프록시에서 DB(session_id/item_id) 기반으로 조립한다.

        /* --- 첨부 파일 메타데이터 조립 (display_name 포함) --- */
        let attachmentsInfo: AttachmentInfo[] | undefined;
        if (uploadResult?.uploaded_files_info && uploadResult.uploaded_files_info.length > 0) {
          attachmentsInfo = uploadResult.uploaded_files_info.map((f) => ({
            path: f.path,
            display_name: f.display_name,
            mime_type: f.mime_type || null,
            file_id: f.file_id || null,
            storage: f.storage || f.source || null,
            source: f.source || null,
          }));
        }

        /* --- 현재 워크스페이스/에이전트 생성 파일(active_file_refs) 조립 --- */
        let activeGeneratedFiles: GeneratedFileInfo[] | undefined;
        let activeFileRefs: ActiveFileRef[] | undefined;
        const genFiles: GeneratedFileInfo[] = [];
        const fileRefs: ActiveFileRef[] = [];
        const seenFileIds = new Set<string>();
        const appendActiveFileRef = (
          fileId: string | null | undefined,
          displayName?: string | null,
          mimeType?: string | null,
        ) => {
          const resolvedFileId = typeof fileId === 'string' ? fileId.trim() : '';
          if (!resolvedFileId || seenFileIds.has(resolvedFileId)) return;
          seenFileIds.add(resolvedFileId);
          fileRefs.push({
            file_id: resolvedFileId,
            display_name: displayName || null,
            mime_type: mimeType || null,
          });
        };

        if (latestConv) {
          for (const m of latestConv.messages) {
            const outputFiles = m.completedData?.output_files;
            if (!outputFiles) continue;
            for (const f of outputFiles) {
              const resolvedFileId = f.file_id || null;
              if (!resolvedFileId || seenFileIds.has(resolvedFileId)) continue;
              appendActiveFileRef(resolvedFileId, f.name || null, f.mime_type || null);
              // 하위 호환 필드: 경로는 서버가 재해석하므로 file_id를 임시 값으로 유지
              genFiles.push({
                file_id: resolvedFileId,
                display_name: f.name,
                internal_path: resolvedFileId,
                mime_type: f.mime_type || null,
                storage: f.storage || null,
              });
            }
          }
        }

        const previewFileId = get().previewFile?.id ?? null;
        const orderedSessionFiles = [...get().sessionFiles].sort((a, b) => {
          if (a.file_id === previewFileId && b.file_id !== previewFileId) return -1;
          if (b.file_id === previewFileId && a.file_id !== previewFileId) return 1;
          return 0;
        });
        for (const workspaceFile of orderedSessionFiles) {
          appendActiveFileRef(
            workspaceFile.file_id,
            workspaceFile.original_name || workspaceFile.stored_name || null,
            workspaceFile.mime_type || null,
          );
        }

        if (genFiles.length > 0) activeGeneratedFiles = genFiles;
        if (fileRefs.length > 0) activeFileRefs = fileRefs;

        /* --- 그룹 채팅 여부 --- */
        const isGroupChat = !!latestConv?.sharedGroup;
        const billingScope: 'personal' | 'team' = billingContext.scope;
        const billingTeamId = billingContext.teamId;

        patchConversationRuntime({
          streamingSessionId: sessionId ?? null,
          streamingMessageId: messageId,
          streamingWorkspaceType: currentMode,
        });

        const gen = streamAgent(
          {
            query: content,
            session_id: sessionId,
            message_id: messageId,
            item_id: convId!,
            stream: true,
            attachments: uploadedPaths,
            attachments_info: attachmentsInfo || null,
            history: null,
            is_group_chat: isGroupChat,
            active_file_refs: activeFileRefs || null,
            active_generated_files: activeGeneratedFiles || null,
            workspace_type: currentMode,
            billing_scope: billingScope,
            billing_team_id: billingTeamId,
            user_locale: getUserLocale(),
            force_mode: options?.reboMode === 'fast' ? 'fast'
              : options?.reboMode === 'plan' ? 'plan'
              : options?.reboMode === 'skill' ? 'skill'
              : null,
            active_skill_ids: options?.reboMode === 'skill' ? (options?.activeSkillIds ?? null) : null,
            active_selection: options?.activeSelection ?? null,
            metadata: options?.requestMetadata ?? null,
          },
          abortController.signal,
        );

        for await (const evt of gen) {
          const s = get();
          const c = s.conversations[convId!];
          if (!c) break;
          const msgs = [...c.messages];
          const idx = msgs.findIndex((m) => m.id === assistantId);
          if (idx === -1) break;
          const msg = { ...msgs[idx] };

          if (evt.status === 'in_progress') {
            const data = (evt as { data: AgentInProgressData }).data;

            /* 검색 결과 카드(event=search_results): detail(객체)을 그대로 보존한
             * 단일 이벤트로 push. thinking 텍스트 누적 경로(아래 step_id 블록)와
             * 분리해야 객체 detail 이 문자열로 뭉개지지 않는다. stepId 는 유지해
             * 직전 thinking 스텝과 같은 블록 근처에 렌더되게 한다. */
            if (data.event === 'search_results') {
              msg.streamEvents = [
                ...msg.streamEvents,
                {
                  id: uuid(),
                  phase: data.phase,
                  event: 'search_results',
                  channel: data.channel ?? undefined,
                  usageSeq: data.usage_seq ?? undefined,
                  isFinal: data.is_final ?? undefined,
                  visibleInTimeline: data.visible_in_timeline ?? true,
                  actor: data.actor ?? undefined,
                  stepId: typeof data.step_id === 'string' ? data.step_id : undefined,
                  title: data.title ?? undefined,
                  summary: data.summary ?? undefined,
                  detail: data.detail,
                  timestamp: Date.now(),
                },
              ];
              msgs[idx] = msg;
              set({
                conversations: {
                  ...s.conversations,
                  [convId!]: { ...c, messages: msgs, updatedAt: Date.now() },
                },
              });
              continue;
            }

            /* 스텝형 진행 narration: step_id 로 그룹핑하여 한 스텝 이벤트에
             * thinking 델타(본문)를 누적한다. 별도 ThinkingBlock blob 대신
             * StreamProgress 가 접이형 스텝(제목→진행→완료)으로 렌더한다. */
            if (typeof data.step_id === 'string' && data.step_id) {
              const stepId = data.step_id;
              const deltaText = typeof data.detail === 'string' ? data.detail : '';
              const events = [...msg.streamEvents];
              const existingIdx = events.findIndex((e) => e.stepId === stepId);
              if (existingIdx >= 0) {
                const existing = events[existingIdx];
                const prevDetail = typeof existing.detail === 'string' ? existing.detail : '';
                events[existingIdx] = {
                  ...existing,
                  title: data.title ?? existing.title,
                  summary: data.title ?? existing.summary,
                  phase: data.phase ?? existing.phase,
                  detail: prevDetail + deltaText,
                  visibleInTimeline:
                    data.visible_in_timeline === true ? true : existing.visibleInTimeline,
                  timestamp: Date.now(),
                };
              } else {
                events.push({
                  id: uuid(),
                  phase: data.phase,
                  event: data.event,
                  channel: data.channel ?? undefined,
                  usageSeq: data.usage_seq ?? undefined,
                  isFinal: data.is_final ?? undefined,
                  visibleInTimeline: data.visible_in_timeline ?? undefined,
                  actor: data.actor ?? undefined,
                  stepId,
                  title: data.title ?? undefined,
                  summary: data.summary ?? data.title ?? undefined,
                  detail: deltaText,
                  timestamp: Date.now(),
                });
              }
              msg.streamEvents = events;
              msgs[idx] = msg;
              set({
                conversations: {
                  ...s.conversations,
                  [convId!]: { ...c, messages: msgs, updatedAt: Date.now() },
                },
              });
              continue;
            }

            /* 추론(thinking) 토큰 → 버퍼에 누적, rAF으로 배치 플러시 (레거시: step_id 미보유) */
            if (isThinkingChannel(data.channel, data.event) && typeof data.detail === 'string') {
              if (shouldSkipLiveChunk('thinking', data)) {
                continue;
              }
              isInThinkingPhase = true;
              thinkingBuf += data.detail;
              if (!msg.isThinking) {
                msg.isThinking = true;
                msgs[idx] = msg;
                set({
                  conversations: {
                    ...s.conversations,
                    [convId!]: { ...c, messages: msgs, updatedAt: Date.now() },
                  },
                });
              }
              if (!thinkingRafHandle) {
                thinkingRafHandle = requestAnimationFrame(flushThinkingBuffer);
              }
              continue;
            }

            /* thinking → non-thinking 전환: 즉시 플러시 + isThinking 해제 */
            if (isInThinkingPhase && !isThinkingChannel(data.channel, data.event)) {
              isInThinkingPhase = false;
              if (thinkingRafHandle) { cancelAnimationFrame(thinkingRafHandle); thinkingRafHandle = 0; }
              const pending = thinkingBuf;
              thinkingBuf = '';
              msg.thinkingContent = (msg.thinkingContent || '') + pending;
              msg.isThinking = false;
              msgs[idx] = msg;
              set({
                conversations: {
                  ...s.conversations,
                  [convId!]: { ...c, messages: msgs, updatedAt: Date.now() },
                },
              });
            }

            /* 최종 응답 토큰 → 버퍼에 누적, rAF으로 배치 플러시 */
            if (
              data.event === 'message' &&
              isAnswerChannel(data.channel, data.actor?.name) &&
              typeof data.detail === 'string'
            ) {
              if (shouldSkipLiveChunk('answer', data)) {
                continue;
              }
              if (isInThinkingPhase) {
                isInThinkingPhase = false;
                if (thinkingRafHandle) { cancelAnimationFrame(thinkingRafHandle); thinkingRafHandle = 0; }
                if (thinkingBuf) {
                  msg.thinkingContent = (msg.thinkingContent || '') + thinkingBuf;
                  thinkingBuf = '';
                }
              }
              msg.isThinking = false;
              msgs[idx] = msg;
              set({
                conversations: {
                  ...s.conversations,
                  [convId!]: { ...c, messages: msgs, updatedAt: Date.now() },
                },
              });
              tokenBuf += data.detail;
              if (!rafHandle) {
                rafHandle = requestAnimationFrame(flushTokenBuffer);
              }
              continue;
            }

            /* widget 채널: widget_end 이벤트에서 위젯 데이터 수집 */
            if (isWidgetChannel(data.channel, data.event) && data.event === 'widget_end' && data.detail) {
              const wd = data.detail as Record<string, unknown>;
              const widgetEntry = {
                widgetId: (wd.widget_id as string) || `w_${Date.now()}`,
                title: (wd.title as string) || 'widget',
                widgetCode: (wd.widget_code as string) || '',
                width: (wd.width as number) || 800,
                height: (wd.height as number) || 600,
                isSvg: (wd.is_svg as boolean) || false,
                isStreaming: false,
              };
              msg.widgets = [...(msg.widgets || []), widgetEntry];
              msgs[idx] = msg;
              set({
                conversations: {
                  ...s.conversations,
                  [convId!]: { ...c, messages: msgs, updatedAt: Date.now() },
                },
              });
              continue;
            }

            /* 세션 ID 추출 */
            if (isBrowserLiveViewPayload(data.detail)) {
              get().applyConversationLiveViewEvent(convId!, data.detail, {
                autoOpen: data.detail.state === 'ready',
                running: data.detail.state === 'ready' && data.detail.available,
              });
            }

            if (
              data.event === 'message' &&
              typeof data.detail === 'object' &&
              data.detail !== null &&
              'session_id' in (data.detail as Record<string, unknown>)
            ) {
              const sid = (data.detail as Record<string, unknown>).session_id as string;
              const prevSid = c.sessionId;
              const convUpdate = { ...c, sessionId: sid };
              msgs[idx] = msg;
              convUpdate.messages = msgs;
              patchConversationRuntime({ streamingSessionId: sid });
              set((prev) => ({
                conversations: { ...prev.conversations, [convId!]: convUpdate },
              }));
              if (sid && sid !== prevSid) {
                void get().loadConversationFiles(convId!);
                syncQueue.enqueue({
                  key: `session_id:${convId}`,
                  execute: async () => { await wsApi.updateItem(convId!, { session_id: sid }); },
                });
              }
              continue;
            }

            /* 스트림 이벤트 기록 */
            const sameDetail = (a: unknown, b: unknown): boolean => {
              if (a === b) return true;
              try {
                return JSON.stringify(a) === JSON.stringify(b);
              } catch {
                return false;
              }
            };
            const streamEvt: StreamEvent = {
              id: uuid(),
              phase: data.phase,
              event: data.event,
              channel: data.channel ?? undefined,
              usageSeq: data.usage_seq ?? undefined,
              isFinal: data.is_final ?? undefined,
              visibleInTimeline: data.visible_in_timeline ?? undefined,
              actor: data.actor ?? undefined,
              summary: data.summary,
              detail: data.detail,
              timestamp: Date.now(),
            };
            const hasSameUsageSeq =
              typeof streamEvt.usageSeq === 'number' &&
              msg.streamEvents.some((evt) => evt.usageSeq === streamEvt.usageSeq);
            const lastStreamEvent = msg.streamEvents[msg.streamEvents.length - 1];
            const isDuplicateLifecycleEvent =
              !!lastStreamEvent &&
              lastStreamEvent.event === streamEvt.event &&
              lastStreamEvent.channel === streamEvt.channel &&
              lastStreamEvent.summary === streamEvt.summary &&
              lastStreamEvent.actor?.type === streamEvt.actor?.type &&
              lastStreamEvent.actor?.name === streamEvt.actor?.name &&
              sameDetail(lastStreamEvent.detail, streamEvt.detail);
            if (!hasSameUsageSeq && !isDuplicateLifecycleEvent) {
              msg.streamEvents = [...msg.streamEvents, streamEvt];
            }
          } else if (evt.status === 'completed') {
            /* 잔여 토큰 즉시 플러시 */
            if (rafHandle) { cancelAnimationFrame(rafHandle); rafHandle = 0; }
            if (tokenBuf) {
              msg.content = normalizeContent(msg.content + tokenBuf);
              tokenBuf = '';
            }
            /* 잔여 thinking 버퍼 플러시 */
            if (thinkingRafHandle) { cancelAnimationFrame(thinkingRafHandle); thinkingRafHandle = 0; }
            if (thinkingBuf) {
              msg.thinkingContent = (msg.thinkingContent || '') + thinkingBuf;
              thinkingBuf = '';
            }
            resetLiveChunkDedup();
            msg.isThinking = false;

            const data = (evt as { data: AgentCompletedData }).data;
            msg.isStreaming = false;
            msg.completedData = data;
            if (isBrowserLiveViewPayload(data.live_view)) {
              get().applyConversationLiveViewEvent(convId!, data.live_view, {
                autoOpen: false,
                running: false,
              });
            } else {
              get().setConversationLiveViewRunning(convId!, false);
            }
            /* content가 있으면 최종 콘텐츠로 교체 */
            if (data.content) {
              msg.content = normalizeContent(data.content);
            }
            /* Generative UI widgets: completed 응답의 widgets를 메시지에 반영 */
            if (data.widgets && Array.isArray(data.widgets) && data.widgets.length > 0) {
              const existingIds = new Set((msg.widgets || []).map((w) => w.widgetId));
              const newWidgets = data.widgets
                .filter((w: Record<string, unknown>) => !existingIds.has(w.widget_id as string))
                .map((w: Record<string, unknown>) => ({
                  widgetId: w.widget_id as string,
                  title: (w.title as string) || 'widget',
                  widgetCode: (w.widget_code as string) || '',
                  width: (w.width as number) || 800,
                  height: (w.height as number) || 600,
                  isSvg: (w.is_svg as boolean) || false,
                  isStreaming: false,
                }));
              if (newWidgets.length > 0) {
                msg.widgets = [...(msg.widgets || []), ...newWidgets];
              }
            }
            const hasOutputFiles = (data.output_files?.length ?? 0) > 0;
            const outputFileIds = (data.output_files || [])
              .map((f) => f.file_id)
              .filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
            if (hasOutputFiles) {
              mergeOptimisticOutputFiles(convId!, data.output_files || [], data.session_id ?? c.sessionId ?? null);
            }

            /* 어시스턴트 응답도 백엔드에 저장 (completedData 포함) */
            if (msg.content) {
              const assistantForSync: ChatMessage = {
                id: msg.id,
                role: 'assistant',
                content: msg.content,
                createdAt: msg.createdAt,
                isStreaming: false,
                streamEvents: [],
                completedData: msg.completedData ?? undefined,
              };
              syncQueue.enqueue({
                key: `message:assistant:${convId}:${msg.id}`,
                execute: async () => {
                  const { syncSaveMessage } = await import('@auw/api-client');
                  const saved = await syncSaveMessage(convId!, assistantForSync);
                  if (!saved) throw new Error('assistant message sync failed');
                  const canonicalMessageId =
                    saved.message_id && typeof saved.message_id === 'string'
                      ? saved.message_id
                      : assistantForSync.id;
                  if (canonicalMessageId !== assistantForSync.id) {
                    const previousMessageId = assistantForSync.id;
                    assistantForSync.id = canonicalMessageId;
                    set((prev) => {
                      const currentConv = prev.conversations[convId!];
                      if (!currentConv) return prev;
                      let touched = false;
                      const nextMessages = currentConv.messages.map((m) => {
                        if (m.id !== previousMessageId) return m;
                        touched = true;
                        return { ...m, id: canonicalMessageId };
                      });
                      if (!touched) return prev;
                      return {
                        conversations: {
                          ...prev.conversations,
                          [convId!]: {
                            ...currentConv,
                            messages: nextMessages,
                            updatedAt: Date.now(),
                          },
                        },
                      };
                    });
                  }
                },
              });
            }

            /* 세션 ID */
            if (data.session_id) {
              const prevSidCompleted = c.sessionId;
              const convUpdate = { ...c, sessionId: data.session_id, updatedAt: Date.now() };
              msgs[idx] = msg;
              convUpdate.messages = msgs;
              const conversations = { ...s.conversations, [convId!]: convUpdate };
              set({ conversations });
              if (data.session_id !== prevSidCompleted) {
                syncQueue.enqueue({
                  key: `session_id:${convId}`,
                  execute: async () => { await wsApi.updateItem(convId!, { session_id: data.session_id }); },
                });
              }
              if (hasOutputFiles) {
                emitAppEvent('storage:agentFilesCreated');
                void refreshConversationFilesWithRetry(convId!, outputFileIds);
              }
              continue;
            }

            if (hasOutputFiles) {
              emitAppEvent('storage:agentFilesCreated');
              void refreshConversationFilesWithRetry(convId!, outputFileIds);
            }
          } else if (evt.status === 'failed') {
            /* 버퍼 정리 */
            if (rafHandle) { cancelAnimationFrame(rafHandle); rafHandle = 0; }
            tokenBuf = '';
            if (thinkingRafHandle) { cancelAnimationFrame(thinkingRafHandle); thinkingRafHandle = 0; }
            thinkingBuf = '';
            resetLiveChunkDedup();
            msg.isThinking = false;

            const message = (evt as { message: { title?: string } }).message;
            msg.isStreaming = false;
            msg.error = message?.title ?? i18n.t('common:error.unknown');
            get().setConversationLiveViewRunning(convId!, false);
            get().addToast({ type: 'error', title: i18n.t('common:toast.agentFail'), description: msg.error ?? undefined });
          }

          msgs[idx] = msg;
          const updated = { ...c, messages: msgs, updatedAt: Date.now() };
          const conversations = { ...s.conversations, [convId!]: updated };
          set({ conversations });
        }

        /* 루프 정상 종료 후 잔여 버퍼 정리 */
        if (rafHandle) { cancelAnimationFrame(rafHandle); rafHandle = 0; }
        if (thinkingRafHandle) { cancelAnimationFrame(thinkingRafHandle); thinkingRafHandle = 0; }
        resetLiveChunkDedup();
      } catch (err: unknown) {
        /* rAF 정리 */
        if (rafHandle) { cancelAnimationFrame(rafHandle); rafHandle = 0; }
        if (thinkingRafHandle) { cancelAnimationFrame(thinkingRafHandle); thinkingRafHandle = 0; }
        tokenBuf = '';
        thinkingBuf = '';
        resetLiveChunkDedup();

        if ((err as Error).name === 'AbortError') return;

        /* 401 / 인증 만료 — refresh 사이클이 끝내 실패한 경우 사용자가
         * ``HTTP 401: 유효하지 않은 JWT 토큰입니다.`` 를 본문 그대로 보던
         * Samsung onprem 회귀를 닫는다. ``authVault`` 의 글로벌 핸들러
         * (``auth-session-invalidated``) 가 로그인 모달 / 재인증 흐름을
         * 띄우므로 chatStore 는 토스트를 한 번만 친절한 카피로 띄우고
         * assistant 메시지에는 raw 401 문자열을 박지 않는다. */
        if (isUnauthorizedHttpError(err)) {
          dispatchAuthSessionInvalidated({ reason: 'chat_stream_401' });
          get().setConversationLiveViewRunning(convId!, false);
          const friendlyMsg = i18n.t('common:error.sessionExpiredDesc');
          get().addToast({
            type: 'error',
            title: i18n.t('common:error.sessionExpired'),
            description: friendlyMsg,
          });
          const s401 = get();
          const c401 = s401.conversations[convId!];
          if (c401) {
            const msgs401 = [...c401.messages];
            const idx401 = msgs401.findIndex((m) => m.id === assistantId);
            if (idx401 !== -1) {
              msgs401[idx401] = { ...msgs401[idx401], isStreaming: false, error: friendlyMsg };
              set({
                conversations: { ...s401.conversations, [convId!]: { ...c401, messages: msgs401 } },
              });
            }
          }
          return;
        }

        const itemNotFound = isWorkspaceItemNotFoundError(err);
        const itemAccessDenied = isWorkspaceItemAccessDeniedError(err);
        const itemUnavailable = isWorkspaceItemUnavailableError(err);
        const errMsg = itemAccessDenied
          ? i18n.t('common:error.chatAccessRevokedDesc')
          : (itemNotFound
            ? i18n.t('common:error.chatSyncRefreshed')
            : (getErrorMessage(err) || i18n.t('common:error.streamingError')));
        get().addToast({
          type: 'error',
          title: itemAccessDenied ? i18n.t('common:error.chatAccessRevoked') : i18n.t('common:error.connectionError'),
          description: errMsg,
        });
        if (itemUnavailable) {
          void get().loadFromBackendApi();
        }
        get().setConversationLiveViewRunning(convId!, false);

        /* 어시스턴트 메시지 에러 처리 */
        const s = get();
        const c = s.conversations[convId!];
        if (c) {
          const msgs = [...c.messages];
          const idx = msgs.findIndex((m) => m.id === assistantId);
          if (idx !== -1) {
            msgs[idx] = { ...msgs[idx], isStreaming: false, error: errMsg };
            const updated = { ...c, messages: msgs };
            set({
              conversations: { ...s.conversations, [convId!]: updated },
            });
          }
        }
      } finally {
        if (streamTimeoutId) {
          clearTimeout(streamTimeoutId);
          streamTimeoutId = null;
        }
        clearConversationRuntime();
      }
    })();
  },

  regenerateMessage: (conversationId, targetMessageId, forceMode) => {
    const state = get();
    const conv = state.conversations[conversationId];
    if (!conv) return;

    const msgIdx = conv.messages.findIndex((m) => m.id === targetMessageId);
    if (msgIdx === -1 || conv.messages[msgIdx].role !== 'assistant') return;

    let userMsg: ChatMessage | null = null;
    for (let i = msgIdx - 1; i >= 0; i--) {
      if (conv.messages[i].role === 'user') {
        userMsg = conv.messages[i];
        break;
      }
    }
    if (!userMsg) return;

    const existingRuntime = getConversationRuntime(state.conversationRuntimes, conversationId);
    if (isConversationRuntimeBusy(existingRuntime)) {
      state.addToast({
        type: 'warning',
        title: i18n.t('common:error.operationInProgress'),
        description: i18n.t('common:error.waitForCompletion'),
      });
      return;
    }

    const billingContext = resolveBillingContext();
    if (billingContext.remainingTokens !== null && billingContext.remainingTokens <= 0) {
      handleTokensExhausted(billingContext.scope);
      return;
    }

    const oldAssistantMsg = conv.messages[msgIdx];
    const newAssistantMsg = makeAssistantMessage();

    set((s) => {
      const c = s.conversations[conversationId]!;
      const msgs = [...c.messages];
      msgs[msgIdx] = { ...oldAssistantMsg, replacedByMessageId: newAssistantMsg.id };
      msgs.splice(msgIdx + 1, 0, newAssistantMsg);
      return {
        conversations: {
          ...s.conversations,
          [conversationId]: { ...c, messages: msgs, updatedAt: Date.now() },
        },
      };
    });

    syncQueue.enqueue({
      key: `message:regenerate-hide:${conversationId}:${oldAssistantMsg.id}`,
      execute: async () => {
        const { syncSaveMessage } = await import('@auw/api-client');
        await syncSaveMessage(conversationId, {
          ...oldAssistantMsg,
          metadata: {
            ...oldAssistantMsg.metadata,
            replaced_by: newAssistantMsg.id,
            status: 'replaced',
          },
        });
      },
    });

    const AGENT_STREAM_TIMEOUT_MS = 30 * 60 * 1000;
    const abortController = new AbortController();
    let streamTimeoutId: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      streamTimeoutId = null;
      abortController.abort();
    }, AGENT_STREAM_TIMEOUT_MS);

    const patchConversationRuntime = (patch: Partial<ConversationRuntimeState>) => {
      set((s) => ({
        conversationRuntimes: patchConversationRuntimeMap(s.conversationRuntimes, conversationId, patch),
      }));
    };
    const clearConversationRuntime = () => {
      set((s) => ({
        conversationRuntimes: patchConversationRuntimeMap(
          s.conversationRuntimes,
          conversationId,
          buildClearedConversationRuntime(getConversationRuntime(s.conversationRuntimes, conversationId)),
        ),
      }));
    };

    patchConversationRuntime({ abortController });

    const assistantId = newAssistantMsg.id;
    const currentMode = conv.mode ?? state.activeMode ?? 'agent';
    const sessionId = conv.sessionId ?? null;
    const messageId = uuid();
    const userContent = userMsg.content;

    (async () => {
      let tokenBuf = '';
      let rafHandle = 0;

      const flushTokenBuffer = () => {
        rafHandle = 0;
        if (!tokenBuf) return;
        const chunk = tokenBuf;
        tokenBuf = '';
        const st = get();
        const cv = st.conversations[conversationId];
        if (!cv) return;
        const ms = [...cv.messages];
        const i = ms.findIndex((m) => m.id === assistantId);
        if (i === -1) return;
        ms[i] = { ...ms[i], content: normalizeContent(ms[i].content + chunk) };
        set({ conversations: { ...st.conversations, [conversationId]: { ...cv, messages: ms, updatedAt: Date.now() } } });
      };

      let thinkingBuf = '';
      let thinkingRafHandle = 0;
      let isInThinkingPhase = false;

      const flushThinkingBuffer = () => {
        thinkingRafHandle = 0;
        if (!thinkingBuf) return;
        const chunk = thinkingBuf;
        thinkingBuf = '';
        const st = get();
        const cv = st.conversations[conversationId];
        if (!cv) return;
        const ms = [...cv.messages];
        const i = ms.findIndex((m) => m.id === assistantId);
        if (i === -1) return;
        ms[i] = { ...ms[i], thinkingContent: (ms[i].thinkingContent || '') + chunk, isThinking: isInThinkingPhase };
        set({ conversations: { ...st.conversations, [conversationId]: { ...cv, messages: ms, updatedAt: Date.now() } } });
      };

      try {
        const isGroupChat = !!conv.sharedGroup;

        let activeFileRefs: ActiveFileRef[] | undefined;
        let activeGeneratedFiles: GeneratedFileInfo[] | undefined;
        const genFiles: GeneratedFileInfo[] = [];
        const fileRefs: ActiveFileRef[] = [];
        const seenFileIds = new Set<string>();
        const appendActiveFileRef = (fileId: string | null | undefined, displayName?: string | null, mimeType?: string | null) => {
          const resolvedFileId = typeof fileId === 'string' ? fileId.trim() : '';
          if (!resolvedFileId || seenFileIds.has(resolvedFileId)) return;
          seenFileIds.add(resolvedFileId);
          fileRefs.push({ file_id: resolvedFileId, display_name: displayName || null, mime_type: mimeType || null });
        };
        const latestConv = get().conversations[conversationId];
        if (latestConv) {
          for (const m of latestConv.messages) {
            const outputFiles = m.completedData?.output_files;
            if (!outputFiles) continue;
            for (const f of outputFiles) {
              const resolvedFileId = f.file_id || null;
              if (!resolvedFileId || seenFileIds.has(resolvedFileId)) continue;
              appendActiveFileRef(resolvedFileId, f.name || null, f.mime_type || null);
              genFiles.push({ file_id: resolvedFileId, display_name: f.name, internal_path: resolvedFileId, mime_type: f.mime_type || null, storage: f.storage || null });
            }
          }
        }
        for (const workspaceFile of get().sessionFiles) {
          appendActiveFileRef(workspaceFile.file_id, workspaceFile.original_name || workspaceFile.stored_name || null, workspaceFile.mime_type || null);
        }
        if (genFiles.length > 0) activeGeneratedFiles = genFiles;
        if (fileRefs.length > 0) activeFileRefs = fileRefs;

        const billingScope: 'personal' | 'team' = billingContext.scope;
        const billingTeamId = billingContext.teamId;

        patchConversationRuntime({
          streamingSessionId: sessionId,
          streamingMessageId: messageId,
          streamingWorkspaceType: currentMode,
        });

        const gen = streamAgent(
          {
            query: userContent,
            session_id: sessionId,
            message_id: messageId,
            item_id: conversationId,
            stream: true,
            attachments: null,
            attachments_info: null,
            history: null,
            is_group_chat: isGroupChat,
            active_file_refs: activeFileRefs || null,
            active_generated_files: activeGeneratedFiles || null,
            workspace_type: currentMode,
            billing_scope: billingScope,
            billing_team_id: billingTeamId,
            user_locale: getUserLocale(),
            force_mode: forceMode,
            is_regenerate: true,
            regenerate_source_message_id: oldAssistantMsg.id,
          },
          abortController.signal,
        );

        for await (const evt of gen) {
          const s = get();
          const c = s.conversations[conversationId];
          if (!c) break;
          const msgs = [...c.messages];
          const idx = msgs.findIndex((m) => m.id === assistantId);
          if (idx === -1) break;
          const msg = { ...msgs[idx] };

          if (evt.status === 'in_progress') {
            const data = (evt as { data: AgentInProgressData }).data;

            if (isThinkingChannel(data.channel, data.event) && typeof data.detail === 'string') {
              isInThinkingPhase = true;
              thinkingBuf += data.detail;
              if (!thinkingRafHandle) thinkingRafHandle = requestAnimationFrame(flushThinkingBuffer);
              continue;
            }

            if (isThinkingChannel(data.channel, data.event) && data.event === 'agent_end') {
              isInThinkingPhase = false;
              if (thinkingRafHandle) { cancelAnimationFrame(thinkingRafHandle); thinkingRafHandle = 0; }
              if (thinkingBuf) { flushThinkingBuffer(); }
              msg.isThinking = false;
              msgs[idx] = msg;
              set({ conversations: { ...s.conversations, [conversationId]: { ...c, messages: msgs, updatedAt: Date.now() } } });
              continue;
            }

            if (isAnswerChannel(data.channel, data.event) && typeof data.detail === 'string') {
              tokenBuf += data.detail;
              if (!rafHandle) rafHandle = requestAnimationFrame(flushTokenBuffer);
              continue;
            }

            if (
              data.event === 'message'
              && typeof data.detail === 'object'
              && data.detail !== null
              && 'session_id' in (data.detail as Record<string, unknown>)
            ) {
              const sid = (data.detail as Record<string, unknown>).session_id as string;
              const prevSid = c.sessionId;
              const convUpdate = { ...c, sessionId: sid };
              msgs[idx] = msg;
              convUpdate.messages = msgs;
              patchConversationRuntime({ streamingSessionId: sid });
              set((prev) => ({ conversations: { ...prev.conversations, [conversationId]: convUpdate } }));
              if (sid && sid !== prevSid) {
                syncQueue.enqueue({
                  key: `session_id:${conversationId}`,
                  execute: async () => { await wsApi.updateItem(conversationId, { session_id: sid }); },
                });
              }
              continue;
            }

            const streamEvt: StreamEvent = {
              id: uuid(),
              phase: data.phase,
              event: data.event,
              channel: data.channel ?? undefined,
              usageSeq: data.usage_seq ?? undefined,
              isFinal: data.is_final ?? undefined,
              visibleInTimeline: data.visible_in_timeline ?? undefined,
              actor: data.actor ?? undefined,
              summary: data.summary,
              detail: data.detail,
              timestamp: Date.now(),
            };
            const hasSameUsageSeq = typeof streamEvt.usageSeq === 'number' && msg.streamEvents.some((e) => e.usageSeq === streamEvt.usageSeq);
            if (!hasSameUsageSeq) {
              msg.streamEvents = [...msg.streamEvents, streamEvt];
            }
          } else if (evt.status === 'completed') {
            if (rafHandle) { cancelAnimationFrame(rafHandle); rafHandle = 0; }
            if (tokenBuf) { msg.content = normalizeContent(msg.content + tokenBuf); tokenBuf = ''; }
            if (thinkingRafHandle) { cancelAnimationFrame(thinkingRafHandle); thinkingRafHandle = 0; }
            if (thinkingBuf) { msg.thinkingContent = (msg.thinkingContent || '') + thinkingBuf; thinkingBuf = ''; }
            msg.isThinking = false;

            const data = (evt as { data: AgentCompletedData }).data;
            msg.isStreaming = false;
            msg.completedData = data;
            if (data.content) msg.content = normalizeContent(data.content);
            if (data.widgets && Array.isArray(data.widgets) && data.widgets.length > 0) {
              const existingIds = new Set((msg.widgets || []).map((w) => w.widgetId));
              const newWidgets = data.widgets
                .filter((w: Record<string, unknown>) => !existingIds.has(w.widget_id as string))
                .map((w: Record<string, unknown>) => ({
                  widgetId: w.widget_id as string,
                  title: (w.title as string) || 'widget',
                  widgetCode: (w.widget_code as string) || '',
                  width: (w.width as number) || 800,
                  height: (w.height as number) || 600,
                  isSvg: (w.is_svg as boolean) || false,
                  isStreaming: false,
                }));
              if (newWidgets.length > 0) {
                msg.widgets = [...(msg.widgets || []), ...newWidgets];
              }
            }
            const hasOutputFiles = (data.output_files?.length ?? 0) > 0;

            if (msg.content) {
              const assistantForSync: ChatMessage = {
                id: msg.id,
                role: 'assistant',
                content: msg.content,
                createdAt: msg.createdAt,
                isStreaming: false,
                streamEvents: [],
                completedData: msg.completedData ?? undefined,
                metadata: { is_regenerate: true, regenerate_source_message_id: oldAssistantMsg.id },
              };
              syncQueue.enqueue({
                key: `message:assistant:${conversationId}:${msg.id}`,
                execute: async () => {
                  const { syncSaveMessage } = await import('@auw/api-client');
                  await syncSaveMessage(conversationId, assistantForSync);
                },
              });
            }

            if (data.session_id) {
              const prevSidCompleted = c.sessionId;
              const convUpdate = { ...c, sessionId: data.session_id, updatedAt: Date.now() };
              msgs[idx] = msg;
              convUpdate.messages = msgs;
              set({ conversations: { ...s.conversations, [conversationId]: convUpdate } });
              if (data.session_id !== prevSidCompleted) {
                syncQueue.enqueue({
                  key: `session_id:${conversationId}`,
                  execute: async () => { await wsApi.updateItem(conversationId, { session_id: data.session_id }); },
                });
              }
              if (hasOutputFiles) {
                emitAppEvent('storage:agentFilesCreated');
                void get().loadConversationFiles(conversationId);
              }
              continue;
            }
            if (hasOutputFiles) {
              emitAppEvent('storage:agentFilesCreated');
              void get().loadConversationFiles(conversationId);
            }
          } else if (evt.status === 'failed') {
            if (rafHandle) { cancelAnimationFrame(rafHandle); rafHandle = 0; }
            tokenBuf = '';
            if (thinkingRafHandle) { cancelAnimationFrame(thinkingRafHandle); thinkingRafHandle = 0; }
            thinkingBuf = '';
            msg.isThinking = false;
            const message = (evt as { message: { title?: string } }).message;
            msg.isStreaming = false;
            msg.error = message?.title ?? i18n.t('common:error.unknown');
            get().addToast({ type: 'error', title: i18n.t('common:toast.agentFail'), description: msg.error ?? undefined });
          }

          msgs[idx] = msg;
          const updated = { ...c, messages: msgs, updatedAt: Date.now() };
          set({ conversations: { ...s.conversations, [conversationId]: updated } });
        }

        if (rafHandle) { cancelAnimationFrame(rafHandle); rafHandle = 0; }
        if (thinkingRafHandle) { cancelAnimationFrame(thinkingRafHandle); thinkingRafHandle = 0; }
      } catch (err: unknown) {
        if (rafHandle) { cancelAnimationFrame(rafHandle); rafHandle = 0; }
        if (thinkingRafHandle) { cancelAnimationFrame(thinkingRafHandle); thinkingRafHandle = 0; }

        /* regenerateMessage 도 streamAgent 위에서 돌아 같은 401 회귀를
         * 받는다. 위 sendMessage catch 와 동일하게 글로벌 핸들러로
         * 위임 + 친절한 카피만 노출. */
        if (isUnauthorizedHttpError(err)) {
          dispatchAuthSessionInvalidated({ reason: 'chat_regenerate_401' });
          const friendlyMsg = i18n.t('common:error.sessionExpiredDesc');
          get().addToast({
            type: 'error',
            title: i18n.t('common:error.sessionExpired'),
            description: friendlyMsg,
          });
          const st401 = get();
          const cv401 = st401.conversations[conversationId];
          if (cv401) {
            const ms401 = [...cv401.messages];
            const i401 = ms401.findIndex((m) => m.id === assistantId);
            if (i401 !== -1) {
              ms401[i401] = { ...ms401[i401], isStreaming: false, error: friendlyMsg };
              set({
                conversations: { ...st401.conversations, [conversationId]: { ...cv401, messages: ms401, updatedAt: Date.now() } },
              });
            }
          }
          return;
        }

        const st = get();
        const cv = st.conversations[conversationId];
        if (cv) {
          const ms = [...cv.messages];
          const i = ms.findIndex((m) => m.id === assistantId);
          if (i !== -1) {
            ms[i] = { ...ms[i], isStreaming: false, error: (err instanceof Error ? err.message : i18n.t('common:error.unknown')) };
            set({ conversations: { ...st.conversations, [conversationId]: { ...cv, messages: ms, updatedAt: Date.now() } } });
          }
        }
      } finally {
        if (streamTimeoutId) { clearTimeout(streamTimeoutId); streamTimeoutId = null; }
        clearConversationRuntime();
      }
    })();
  },

  stopStreaming: (conversationId) => {
    const state = get();
    const targetConversationId = conversationId === undefined ? state.activeId : conversationId;
    const runtime = getConversationRuntime(state.conversationRuntimes, targetConversationId);
    if (!targetConversationId || !runtime) {
      return;
    }

    if (runtime.abortController) {
      runtime.abortController.abort();
    }

    if (runtime.streamingSessionId || runtime.streamingMessageId) {
      cancelAgentStream({
        sessionId: runtime.streamingSessionId ?? undefined,
        messageId: runtime.streamingMessageId ?? undefined,
        workspaceType: runtime.streamingWorkspaceType ?? undefined,
      });
    }

    set((s) => ({
      conversationRuntimes: patchConversationRuntimeMap(
        s.conversationRuntimes,
        targetConversationId,
        buildClearedConversationRuntime(
          getConversationRuntime(s.conversationRuntimes, targetConversationId),
        ),
      ),
    }));

    /* 현재 스트리밍 중인 메시지를 완료 처리 */
    const latestState = get();
    const c = latestState.conversations[targetConversationId];
    if (c) {
      const msgs = c.messages.map((m) =>
        (m.isStreaming || m.isThinking) ? { ...m, isStreaming: false, isThinking: false } : m,
      );
      const updated = { ...c, messages: msgs };
      set({ conversations: { ...latestState.conversations, [targetConversationId]: updated } });
    }
  },

  toggleReaction: (conversationId, messageId, emoji, userId) => {
    const s = get();
    const conv = s.conversations[conversationId];
    if (!conv) return;

    let updatedReactions: Reaction[] = [];

    const msgs = conv.messages.map((m) => {
      if (m.id !== messageId) return m;
      const reactions: Reaction[] = [...(m.reactions || [])];
      const idx = reactions.findIndex((r) => r.emoji === emoji);

      if (idx === -1) {
        reactions.push({ emoji, userIds: [userId] });
      } else {
        const r = reactions[idx];
        if (r.userIds.includes(userId)) {
          const filtered = r.userIds.filter((id) => id !== userId);
          if (filtered.length === 0) {
            reactions.splice(idx, 1);
          } else {
            reactions[idx] = { ...r, userIds: filtered };
          }
        } else {
          reactions[idx] = { ...r, userIds: [...r.userIds, userId] };
        }
      }

      updatedReactions = reactions;
      return { ...m, reactions };
    });

    set({
      conversations: {
        ...s.conversations,
        [conversationId]: { ...conv, messages: msgs, updatedAt: Date.now() },
      },
    });

    // 그룹채팅이면 백엔드 동기화 + WebSocket 브로드캐스트
    if (conv.sharedGroup) {
      wsApi.updateMessageMetadata(conversationId, messageId, {
        reactions: updatedReactions,
      }).then((updated) => {
        const canonicalMessageId =
          (updated?.message_id && String(updated.message_id)) || messageId;

        if (canonicalMessageId !== messageId) {
          set((prev) => {
            const currentConv = prev.conversations[conversationId];
            if (!currentConv) return prev;
            let touched = false;
            const nextMessages = currentConv.messages.map((m) => {
              if (m.id !== messageId) return m;
              touched = true;
              return { ...m, id: canonicalMessageId, reactions: updatedReactions };
            });
            if (!touched) return prev;
            return {
              conversations: {
                ...prev.conversations,
                [conversationId]: {
                  ...currentConv,
                  messages: nextMessages,
                  updatedAt: Date.now(),
                },
              },
            };
          });
        }

        collabWS.sendReactionUpdate(conversationId, canonicalMessageId, updatedReactions);
      }).catch((err) => {
        console.error('[ChatStore] 리액션 동기화 실패:', err);
      });
    }
  },

  applyConversationLiveViewEvent: (conversationId, liveView, options) => {
    if (!conversationId || !isBrowserLiveViewPayload(liveView)) return;
    const persistedLiveView = normalizeConversationLiveView(liveView);
    if (!persistedLiveView) return;

    let changed = false;
    set((state) => {
      const conversation = state.conversations[conversationId];
      if (!conversation) return state;
      const runtime = getConversationRuntime(state.conversationRuntimes, conversationId);
      const nextRuntimeLiveView = buildNextLiveViewRuntime(runtime, liveView, options);
      const prevConversationLiveView = conversation.liveView ?? null;
      const liveViewChanged = JSON.stringify(prevConversationLiveView) !== JSON.stringify(persistedLiveView);
      const runtimeChanged = JSON.stringify(runtime?.liveView ?? null) !== JSON.stringify(nextRuntimeLiveView);
      if (!liveViewChanged && !runtimeChanged) return state;
      changed = true;
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conversation,
            liveView: persistedLiveView,
            updatedAt: Date.now(),
          },
        },
        conversationRuntimes: patchConversationRuntimeMap(
          state.conversationRuntimes,
          conversationId,
          { liveView: nextRuntimeLiveView },
        ),
      };
    });

    if (changed) {
      enqueueConversationSettingsSync(get, conversationId);
    }
  },

  setConversationLiveViewOpen: (conversationId, open) => {
    if (!conversationId) return;
    set((state) => {
      const conversation = state.conversations[conversationId];
      if (!conversation?.liveView?.liveViewRef) return state;
      const runtime = getConversationRuntime(state.conversationRuntimes, conversationId);
      const currentLiveView = runtime?.liveView ?? {
        ...EMPTY_CONVERSATION_LIVE_VIEW_RUNTIME,
        liveViewRef: conversation.liveView.liveViewRef,
        available: conversation.liveView.available,
        watchOnly: conversation.liveView.watchOnly,
        state: conversation.liveView.state,
        errorMessage: conversation.liveView.errorMessage ?? null,
      };
      if (currentLiveView.open === open && !(open && state.activeId === conversationId && state.previewFile)) {
        return state;
      }

      const nextState: Partial<ChatState> = {
        conversationRuntimes: patchConversationRuntimeMap(
          state.conversationRuntimes,
          conversationId,
          { liveView: { ...currentLiveView, open } },
        ),
      };

      if (open && state.activeId === conversationId) {
        nextState.previewFile = null;
        nextState.previewFullscreen = false;
        nextState.narrowPreviewView = 'preview';
      }

      return nextState;
    });
  },

  setConversationLiveViewRunning: (conversationId, running) => {
    if (!conversationId) return;
    set((state) => {
      const conversation = state.conversations[conversationId];
      const runtime = getConversationRuntime(state.conversationRuntimes, conversationId);
      const currentLiveView = runtime?.liveView ?? (
        conversation?.liveView?.liveViewRef
          ? {
              ...EMPTY_CONVERSATION_LIVE_VIEW_RUNTIME,
              liveViewRef: conversation.liveView.liveViewRef,
              available: conversation.liveView.available,
              watchOnly: conversation.liveView.watchOnly,
              state: conversation.liveView.state,
              errorMessage: conversation.liveView.errorMessage ?? null,
            }
          : null
      );
      if (!currentLiveView || currentLiveView.running === running) return state;
      return {
        conversationRuntimes: patchConversationRuntimeMap(
          state.conversationRuntimes,
          conversationId,
          { liveView: { ...currentLiveView, running } },
        ),
      };
    });
  },

  syncCurrentUserProfile: ({ userId, name, email, picture }) => {
    if (!userId) return;
    set((s) => {
      let changed = false;
      const conversations = Object.fromEntries(
        Object.entries(s.conversations).map(([convId, conv]) => {
          let nextConv = conv;

          if (conv.sharedGroup?.members?.length) {
            let membersChanged = false;
            const nextMembers = conv.sharedGroup.members.map((member) => {
              if (member.user_id !== userId) return member;
              const updated = {
                ...member,
                ...(name !== undefined ? { name } : {}),
                ...(email !== undefined ? { email } : {}),
                ...(picture !== undefined ? { picture } : {}),
              };
              if (
                updated.name !== member.name
                || updated.email !== member.email
                || updated.picture !== member.picture
              ) {
                membersChanged = true;
              }
              return updated;
            });
            if (membersChanged) {
              changed = true;
              nextConv = {
                ...nextConv,
                sharedGroup: { ...nextConv.sharedGroup!, members: nextMembers },
              };
            }
          }

          const messageNameChanged = name !== undefined && conv.messages.some(
            (m) => m.senderId === userId && m.senderName !== name,
          );
          if (messageNameChanged) {
            changed = true;
            const nextMessages = conv.messages.map((m) => (
              m.senderId === userId ? { ...m, senderName: name } : m
            ));
            nextConv = { ...nextConv, messages: nextMessages };
          }

          return [convId, nextConv];
        }),
      );

      if (!changed) return s;
      return { conversations };
    });
  },

  /* ---------- getters ---------- */

  activeConversation: () => {
    const { activeId, activeMode, conversations } = get();
    if (!activeId) return null;
    const conv = conversations[activeId] ?? null;
    if (!conv) return null;
    // mode-mismatch 가드 — 다른 chat surface 의 conv 가 새지 않도록.
    // ----------------------------------------------------------------
    // 사고 시나리오 (실제 보고된 회귀):
    //   1) 사용자가 doc-agent 페이지에서 채팅 → ``setActiveConversation(itemId)``
    //      가 ``activeId=itemId`` + ``activeMode='doc-agent'`` 를 동기화
    //      하고, ``conversations[itemId].mode='doc-agent'`` 가 적재된다.
    //   2) 사용자가 사이드바 등으로 통합 에이전트 페이지로 이동 →
    //      ``setActiveMode('agent')`` 만 호출되고 ``activeId`` 는
    //      doc-agent itemId 그대로 남는다 (mode 별 마지막 active 보존
    //      목적의 의도된 동작 — useRouteSync 의 mode 일치 검사 분기 참조).
    //   3) 새로고침 → zustand persist 가 ``activeId`` / ``activeMode`` /
    //      ``conversations`` 모두 복원. 그 결과 ``activeMode='agent'``
    //      이지만 ``activeId`` 가 가리키는 conv.mode 는 'doc-agent'.
    //   4) ``ChatArea`` / ``WorkspaceSplitLayout`` / ``AgentContent`` 가
    //      ``activeMessages()`` → ``activeConversation()`` 결과의
    //      messages 를 그대로 그림 → **통합 에이전트 페이지에 doc-agent
    //      채팅 버블이 새서 그려짐**.
    //
    // 호출자 측에서 일일이 mode 체크를 강요하지 않고, getter 자체가
    // 표면 동치성을 보장한다. ``sendMessage`` 본체의
    // ``widget|feed|tools → agent`` normalization (line 1428) 과 같은
    // chat-surface 그룹화를 그대로 따른다 — 이 셋은 ``ChatArea`` 가
    // 공유하는 표면이라 conv.mode 가 그 중 하나면 'agent' 와 동치.
    if (
      activeMode
      && conv.mode
      && !sameChatSurface(conv.mode, activeMode)
    ) {
      return null;
    }
    return conv;
  },

  activeMessages: () => {
    // ``activeConversation`` 의 mode-mismatch 가드를 그대로 상속.
    const conv = get().activeConversation();
    return conv?.messages ?? [];
  },

  isStreaming: (conversationId) => {
    const targetConversationId = conversationId === undefined ? get().activeId : conversationId;
    const runtime = getConversationRuntime(get().conversationRuntimes, targetConversationId);
    return isConversationRuntimeStreaming(runtime);
  },
  isUploadingConversation: (conversationId) => {
    const targetConversationId = conversationId === undefined ? get().activeId : conversationId;
    const runtime = getConversationRuntime(get().conversationRuntimes, targetConversationId);
    return runtime?.isUploading ?? false;
  },
  setUploading: (v) => set({ isUploading: v }),
}));

export function resetChatRuntimeForLogout(): void {
  prefetchingIds.clear();
  clearMessageHistoryCache();
  conversationFilesInFlight.clear();
  cancelConversationMemorySweep();
  clearSelectionLoadingIndicator();
  loadFromBackendInFlight = null;
  initialFastBootstrapDone = false;
  initialFullSyncScheduled = false;
  const conversationRuntimes = useChatStore.getState().conversationRuntimes;
  Object.values(conversationRuntimes).forEach((runtime) => {
    runtime.abortController?.abort();
  });
  useChatStore.setState({
    conversations: {},
    conversationOrder: [],
    workspaceOrder: [],
    conversationRuntimes: {},
    activeId: null,
    loadingConversationId: null,
    sessionFiles: [],
    previewFile: null,
    previewFullscreen: false,
    totalItemCount: 0,
    totalItemCountByMode: {},
    loadingMoreItems: false,
    workspaceHydrated: false,
  });
  useWorkspaceNavigationStore.getState().resetWorkspaceNavigation();
}

